import React, { useState, useMemo, useEffect } from 'react';
import { Search, ArrowLeft, Home, Settings, Upload, Plus, Edit2, Trash2, Users, Download, FileJson } from 'lucide-react';
import * as XLSX from 'xlsx';
import { database } from './firebase';
import { ref, set, onValue, get } from 'firebase/database';
import { auth } from './firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { getAdminCredentials } from './utils/credentials';

const hashPassword = async (password) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// Hash chuẩn của password (tạo sẵn)
const ADMIN_PASSWORD_HASH = 'ce658ae4e00cddab8d2f719b343cb22d714fa673eebe8f867bb4e4da1842d3b2';
const removeVietnameseTones = (str) => {
  if (!str) return '';
  str = str.toLowerCase();
  str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a');
  str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e');
  str = str.replace(/ì|í|ị|ỉ|ĩ/g, 'i');
  str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o');
  str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u');
  str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y');
  str = str.replace(/đ/g, 'd');
  str = str.replace(/\s+/g, ' ');
  return str.trim();
};
// Helper functions để chuyển đổi dữ liệu cho Firebase
const convertToFirebase = (rooms) => {
  if (!rooms || !Array.isArray(rooms)) {
    console.log('convertToFirebase: Invalid input', rooms);
    return [];
  }
  
  try {
    return rooms.map(room => {
      if (!room) return null;
      
      return {
        ...room,
        members: Array.isArray(room.members) ? room.members.map(member => {
          if (!member) return null;
          
          return {
            ...member,
            points: member.points && typeof member.points === 'object'
              ? Object.entries(member.points).reduce((acc, [key, value]) => {
                  acc[key.replace(/\//g, '_')] = value;
                  return acc;
                }, {})
              : {}
          };
        }).filter(Boolean) : []
      };
    }).filter(Boolean);
  } catch (error) {
    console.error('convertToFirebase error:', error);
    return [];
  }
};

const convertFromFirebase = (rooms) => {
  // Kiểm tra đầu vào
  if (!rooms || !Array.isArray(rooms)) {
    console.log('convertFromFirebase: Invalid input', rooms);
    return [];
  }
  
  try {
    return rooms.map(room => {
      if (!room) return null;
      
      return {
        ...room,
        members: Array.isArray(room.members) ? room.members.map(member => {
          if (!member) return null;
          
          return {
            ...member,
            points: member.points && typeof member.points === 'object' 
              ? Object.entries(member.points).reduce((acc, [key, value]) => {
                  acc[key.replace(/_/g, '/')] = value;
                  return acc;
                }, {})
              : {}
          };
        }).filter(Boolean) : []
      };
    }).filter(Boolean);
  } catch (error) {
    console.error('convertFromFirebase error:', error);
    return [];
  }
};
// ✅ Hàm kiểm tra và set admin session
// ✅ Hàm kiểm tra và set admin session - PHIÊN BẢN CẢI TIẾN
const checkAndSetAdminSession = async (database) => {
  const sessionRef = ref(database, 'adminSession');
  const mySessionId = Date.now().toString() + Math.random().toString(36);
  
  try {
    const snapshot = await get(sessionRef);
    const currentSession = snapshot.val();
    
    // ✅ THAY ĐỔI 1: Giảm timeout xuống 15 phút
    const SESSION_TIMEOUT = 15 * 60 * 1000; // 15 phút (thay vì 1 giờ)
    
    // ✅ THAY ĐỔI 2: Cho phép "đá" session cũ
    if (currentSession && Date.now() - currentSession.timestamp < SESSION_TIMEOUT) {
      const confirmForceLogin = window.confirm(
        '⚠️ Đã có phiên đăng nhập khác đang hoạt động.\n\n' +
        `Thiết bị: ${currentSession.device || 'Không xác định'}\n` +
        `Thời gian đăng nhập: ${currentSession.loginTime}\n\n` +
        '👉 Bạn có muốn ĐÁ phiên đăng nhập cũ và tiếp tục không?'
      );
      
      if (!confirmForceLogin) {
        return { 
          success: false, 
          message: 'Bạn đã hủy đăng nhập' 
        };
      }
      
      // ✅ Đánh dấu session cũ bị đá
      await set(sessionRef, {
        ...currentSession,
        forceLogout: true,
        forceLogoutTime: Date.now()
      });
      
      // Đợi 1 giây để thiết bị cũ nhận tín hiệu
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    // ✅ THAY ĐỔI 3: Lưu thông tin thiết bị
    const deviceInfo = /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent) 
      ? '📱 Điện thoại' 
      : '💻 Máy tính';
    
    const browserInfo = navigator.userAgent.includes('Chrome') ? 'Chrome' :
                       navigator.userAgent.includes('Firefox') ? 'Firefox' :
                       navigator.userAgent.includes('Safari') ? 'Safari' : 'Khác';
    
    // Set session mới
    await set(sessionRef, {
      sessionId: mySessionId,
      timestamp: Date.now(),
      loginTime: new Date().toLocaleString('vi-VN'),
      device: `${deviceInfo} - ${browserInfo}`,
      forceLogout: false
    });
    
    // Lưu session ID vào sessionStorage
    sessionStorage.setItem('adminSessionId', mySessionId);
    
    return { success: true };
  } catch (error) {
    console.error('Error checking admin session:', error);
    return { 
      success: false, 
      message: 'Lỗi khi kiểm tra phiên đăng nhập: ' + error.message 
    };
  }
};
// Sample data for rooms
const initialRooms = [
  {
    id: 1,
    name: '[1-1 RETURN] ROOM LỊCH',
    icon: '🏠',
    qrCode: null,
    password: '',
    rule: [
      { min: 500000, max: 1000000, points: 0.5 },
      { min: 1000000, max: 2000000, points: 1 },
      { min: 2000000, max: 5000000, points: 2 },
      { min: 5000000, max: 10000000, points: 3 },
    ],
    members: [
      { 
        id: 0, 
        name: 'A Đức Airport', 
        points: { 
          '07/10': 0,    // ✅ Ngày cũ = 0
          '08/10': 0,    // ✅ Ngày hôm qua = 0
          '09/10': 0.5   // ✅ Ngày hôm nay = 0.5
        }, 
        totalPoints: 0.5,
        deadline: 'Tháng 12/2025', 
        note: 'RET1' 
      },
      { 
        id: 2, 
        name: 'Bin Lê', 
        points: { 
          '07/10': 0,    // ✅ Ngày cũ = 0
          '08/10': 5.0,  // ✅ Ngày hôm qua = 5.0
          '09/10': 5.0   // ✅ Ngày hôm nay = 5.0
        }, 
        totalPoints: 5.0,
        deadline: 'Tháng 12/2025', 
        note: 'RET1' 
      },
      { 
        id: 5, 
        name: 'Đặng Văn Khánh', 
        points: { 
          '07/10': 0,    // ✅ Ngày cũ = 0
          '08/10': 1.0,  // ✅ Ngày hôm qua = 1.0
          '09/10': 1.0   // ✅ Ngày hôm nay = 1.0
        }, 
        totalPoints: 1.0,
        deadline: 'Tháng 12/2025', 
        note: 'RET1' 
      },
      { 
        id: 8, 
        name: 'Hiếu Hán Linh', 
        points: { 
          '07/10': 0,     // ✅ Ngày cũ = 0
          '08/10': -1.0,  // ✅ Ngày hôm qua = -1.0
          '09/10': -1.0   // ✅ Ngày hôm nay = -1.0
        }, 
        totalPoints: -1.0,
        deadline: 'Tháng 12/2025', 
        note: 'RET1' 
      },
    ],
    transactions: {
      2: [
        { date: '24/09', description: 'Ngày 28/09 - 30/09: 29 chỗ (1 xe) thaco - Chiều tối 28 đón sg--- báo tộc...', price: 8500000, role: 'Giao', partner: 'Bin Lê', points: 3 },
        { date: '24/09', description: 'Cần 2 timo 9c 2024-2025 Ngày 27/9. Lúc 14h Đón Sân Bay về Q1...', price: 19000000, role: 'Giao', partner: 'Tuyết Nhung', points: 5 },
      ],
      5: [
        { date: '24/09', description: 'Giao hàng xe 29c', price: 11500000, role: 'Giao', partner: 'Luyên Hồng', points: 5 },
      ],
    }
  },
  {
    id: 2,
    name: '[1_1 RET 2] ROOM LỊCH',
    icon: '🏢',
    qrCode: null,
    rule: [
      { min: 500000, max: 1000000, points: 0.5 },
      { min: 1000000, max: 2000000, points: 1 },
      { min: 2000000, max: 5000000, points: 2 },
      { min: 5000000, max: 10000000, points: 3 },
    ],
    members: [
      { 
        id: 0, 
        name: 'A Đức Airport', 
        points: { '07/10': 0, '08/10': 0, '09/10': 0.5 }, 
        totalPoints: 0.5, 
        deadline: 'Tháng 12/2025', 
        note: 'RET1' 
      },
      { 
        id: 2, 
        name: 'Bin Lê', 
        points: { '07/10': 0, '08/10': 5.0, '09/10': 5.0 }, 
        totalPoints: 5.0, 
        deadline: 'Tháng 12/2025', 
        note: 'RET1' 
      },
      { 
        id: 5, 
        name: 'Đặng Văn Khánh', 
        points: { '07/10': 0, '08/10': 1.0, '09/10': 1.0 }, 
        totalPoints: 1.0, 
        deadline: 'Tháng 12/2025', 
        note: 'RET1' 
      },
      { 
        id: 8, 
        name: 'Hiếu Hán Linh', 
        points: { '07/10': 0, '08/10': -1.0, '09/10': -1.0 }, 
        totalPoints: -1.0, 
        deadline: 'Tháng 12/2025', 
        note: 'RET1' 
      },
    ],
    transactions: {}
  }
];

const RoomManagementSystem = () => {
  const [currentView, setCurrentView] = useState('home');
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [isFirebaseAuthenticated, setIsFirebaseAuthenticated] = useState(false);
  const [isLoadingFromFirebase, setIsLoadingFromFirebase] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [visitCount, setVisitCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [adminRoomSearchTerms, setAdminRoomSearchTerms] = useState({});
  const [transactionForm, setTransactionForm] = useState({
    roomId: null,
    date: new Date().toISOString().split('T')[0],
    delivererId: '',
    receiverId: '',
    price: '',
    description: '',
    manualPoints: '',
    isAddPointTransaction: false,
    isFreeTransaction: false
  });
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [memberForm, setMemberForm] = useState({
    roomId: null,
    id: '',
    name: '',
    deadline: '',
    note: '',
    initialPoints: '0'
  });
  const [showRoomForm, setShowRoomForm] = useState(false);
  const [roomForm, setRoomForm] = useState({
    name: '',
    icon: '🏠',
    password: '',
    rule: [
      { min: 500000, max: 1000000, points: 0.5 },
      { min: 1000000, max: 2000000, points: 1 },
      { min: 2000000, max: 5000000, points: 2 },
      { min: 5000000, max: 10000000, points: 3 },
    ]
  });
  const [editingRoom, setEditingRoom] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  const [showDeleteMemberConfirm, setShowDeleteMemberConfirm] = useState(null);
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [selectedRoomTransactions, setSelectedRoomTransactions] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [showQRUpload, setShowQRUpload] = useState(null);
  const [showMemberHistory, setShowMemberHistory] = useState(null);
  const [editingHistoryTransaction, setEditingHistoryTransaction] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [delivererSearch, setDelivererSearch] = useState('');
  const [receiverSearch, setReceiverSearch] = useState('');
  const [showDelivererDropdown, setShowDelivererDropdown] = useState(false);
  const [showReceiverDropdown, setShowReceiverDropdown] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [showSystemStatus, setShowSystemStatus] = useState(false);
  //const [showMemberHistory, setShowMemberHistory] = useState(null);
  //const [editingHistoryTransaction, setEditingHistoryTransaction] = useState(null);
// Ctrl + Shift + X
useEffect(() => {
  const handleKeyPress = (e) => {
    if (e.ctrlKey && e.shiftKey && e.key === 'X') {
      setCurrentView('adminLogin');
    }
  };
  window.addEventListener('keydown', handleKeyPress);
  return () => window.removeEventListener('keydown', handleKeyPress);
}, []);
  
// Hiển thị QR
useEffect(() => {
  if (currentView === 'room' && selectedRoom) {
    const updatedRoom = rooms.find(r => r.id === selectedRoom.id);
    if (updatedRoom) {
      setSelectedRoom(updatedRoom);
    }
  }
}, [rooms, currentView]);
// Đóng dropdown khi click bên ngoài
useEffect(() => {
  const handleClickOutside = (event) => {
    const target = event.target;
    // Kiểm tra nếu click không phải trong dropdown
    if (!target.closest('.relative')) {
      setShowDelivererDropdown(false);
      setShowReceiverDropdown(false);
    }
  };

  if (showDelivererDropdown || showReceiverDropdown) {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }
}, [showDelivererDropdown, showReceiverDropdown]);
  
// Tăng visit counter trên Firebase (1 lần mỗi session)
useEffect(() => {
  const hasVisited = sessionStorage.getItem('hasVisited');
  
  if (!hasVisited) {
    const counterRef = ref(database, 'visitCount');
    
    // Đọc giá trị hiện tại và tăng lên 1
    get(counterRef).then((snapshot) => {
      const currentCount = snapshot.val() || 0;
      set(counterRef, currentCount + 1)
        .then(() => console.log('✅ Visit count increased'))
        .catch(error => console.error('❌ Error updating visit count:', error));
    });
    
    // Đánh dấu đã đếm trong session này
    sessionStorage.setItem('hasVisited', 'true');
  }
}, []);
// Lắng nghe thay đổi visit counter real-time
useEffect(() => {
  const counterRef = ref(database, 'visitCount');
  
  const unsubscribe = onValue(counterRef, (snapshot) => {
    const count = snapshot.val() || 0;
    setVisitCount(count);
    console.log('📊 Visit count updated:', count);
  });
  
  return () => unsubscribe();
}, []);
// Kiểm tra trạng thái đăng nhập Firebase
useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, (user) => {
    if (user) {
      //console.log('Firebase user logged in:', user.email);
      setIsFirebaseAuthenticated(true);
    } else {
      //console.log('Firebase user logged out');
      setIsFirebaseAuthenticated(false);
    }
  });
  
  return () => unsubscribe();
}, []);
// Đọc dữ liệu từ Firebase (chỉ 1 lần khi mount)
useEffect(() => {
  const roomsRef = ref(database, 'rooms');
  
  get(roomsRef).then((snapshot) => {
    const data = snapshot.val();
    console.log('🔵 Loaded from Firebase:', data ? `${data.length} rooms` : 'null');
    
    if (data && Array.isArray(data)) {
      setIsLoadingFromFirebase(true);
      const converted = convertFromFirebase(data);
      if (converted && converted.length > 0) {
        console.log('🔵 Setting rooms:', converted.length);
        setRooms(converted);
      } else {
        // Nếu Firebase trống, dùng initialRooms
        setRooms(initialRooms);
      }
      setTimeout(() => setIsLoadingFromFirebase(false), 500);
    } else {
      // Nếu Firebase không có dữ liệu, dùng initialRooms
      setRooms(initialRooms);
    }
    
    // Tắt loading sau khi load xong
    setIsLoading(false);
  }).catch(error => {
    console.error('Firebase read error:', error);
    // Nếu lỗi, dùng initialRooms và tắt loading
    setRooms(initialRooms);
    setIsLoading(false);
  });
}, []);

// Lưu dữ liệu lên Firebase
useEffect(() => {
  // KHÔNG lưu nếu đang load từ Firebase
  if (isLoadingFromFirebase) return;
  if (!rooms || rooms.length === 0 || !isFirebaseAuthenticated) return;
  
  const timer = setTimeout(() => {
    const roomsRef = ref(database, 'rooms');
    const firebaseData = convertToFirebase(rooms);
    
    if (firebaseData && firebaseData.length > 0) {
      console.log('Saving to Firebase:', new Date().toLocaleTimeString());
      set(roomsRef, firebaseData).catch(error => {
        console.error('Firebase set error:', error);
      });
    }
  }, 500);
  
  return () => clearTimeout(timer);
}, [rooms, isFirebaseAuthenticated, isLoadingFromFirebase]);


// ✅ Auto cleanup session khi đóng tab/trình duyệt - CẢI TIẾN
useEffect(() => {
  const handleBeforeUnload = (e) => {
    const mySessionId = sessionStorage.getItem('adminSessionId');
    
    if (mySessionId && isAdminAuthenticated) {
      // ✅ Dùng Firebase SDK thay vì fetch() để tránh lỗi permission
      const sessionRef = ref(database, 'adminSession');
      
      // Clear session bằng Firebase set(null)
      set(sessionRef, null).catch(err => console.error('Cleanup error:', err));
      
      // Clear local data
      sessionStorage.removeItem('adminSessionId');
      const heartbeatInterval = sessionStorage.getItem('heartbeatInterval');
      if (heartbeatInterval) {
        clearInterval(parseInt(heartbeatInterval));
        sessionStorage.removeItem('heartbeatInterval');
      }
      
      console.log('🧹 Cleaning up session on tab close');
    }
  };
  
  // ✅ Lắng nghe cả beforeunload và visibilitychange
  window.addEventListener('beforeunload', handleBeforeUnload);
  
  // ✅ Thêm: Cleanup khi tab bị ẩn (mobile/background)
  const handleVisibilityChange = () => {
    if (document.hidden && isAdminAuthenticated) {
      console.log('📴 Tab hidden - session still active');
    }
  };
  
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}, [isAdminAuthenticated]);


const currentDate = new Date().toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  // Hàm lọc thành viên theo tên

// ✅ Hàm lọc thành viên theo tên - CẢI TIẾN với tìm kiếm không dấu
  const filterMembers = (members, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') return members;
    
    const searchNormalized = removeVietnameseTones(searchTerm.toLowerCase());
    
    return members.filter(member => {
      const memberNameNormalized = removeVietnameseTones(member.name.toLowerCase());
      const memberIdStr = member.id.toString();
      
      return memberNameNormalized.includes(searchNormalized) || 
            memberIdStr.includes(searchTerm);
    });
  };
  const parseTransactionDate = (dateStr) => {
    if (!dateStr) return new Date(0);
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
    } else if (parts.length === 2) {
      return new Date(new Date().getFullYear(), parseInt(parts[1]) - 1, parseInt(parts[0]));
    }
    return new Date(0);
  };
  const getDateColumns = () => {
    const today = new Date();
    const dates = [];
    for (let i = 2; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const formatted = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
      dates.push(formatted);
    }
    return dates;
  };

  const dateColumns = getDateColumns();
 
 // ✅ FILTERED MEMBERS - CẢI TIẾN VỚI TÌM KIẾM KHÔNG DẤU
  const filteredMembers = useMemo(() => {
    if (!selectedRoom) return [];
    
    if (!searchTerm || searchTerm.trim() === '') {
      return selectedRoom.members;
    }
    
    const searchNormalized = removeVietnameseTones(searchTerm.toLowerCase());
    
    return selectedRoom.members.filter(member => {
      const memberNameNormalized = removeVietnameseTones(member.name.toLowerCase());
      const memberIdStr = member.id.toString();
      
      return memberNameNormalized.includes(searchNormalized) || 
            memberIdStr.includes(searchTerm);
    });
  }, [selectedRoom, searchTerm]);

  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);
  const paginatedMembers = filteredMembers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getPointColor = (point) => {
    if (point > 0) return 'text-blue-600';
    if (point < 0) return 'text-red-600';
    return 'text-gray-900';
  };

  const handleMemberClick = (member) => {
    setSelectedMember(member);
    setShowModal(true);
  };

const handleRoomClick = (room) => {
  // Nếu room có password, hiển thị modal nhập password
  if (room.password && room.password.trim() !== '') {
    setShowPasswordModal(room);
    setPasswordInput('');
    setPasswordError('');
  } else {
    // Không có password, vào thẳng
    setSelectedRoom(room);
    setCurrentView('room');
    setSearchTerm('');
    setCurrentPage(1);
  }
};
const handlePasswordSubmit = () => {
  if (passwordInput === showPasswordModal.password) {
    // Đúng password
    setSelectedRoom(showPasswordModal);
    setCurrentView('room');
    setSearchTerm('');
    setCurrentPage(1);
    setShowPasswordModal(null);
    setPasswordInput('');
    setPasswordError('');
  } else {
    // Sai password
    setPasswordError('Mật khẩu không đúng!');
  }
};

  const handleExcelUpload = (event, roomId) => {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      
      // Đọc sheet tổng hợp dạng array
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawData = XLSX.utils.sheet_to_json(firstSheet, { header: 1, defval: '' });
      
      // Bỏ dòng header (dòng 0)
      const dataRows = rawData.slice(1);
      
     const newMembers = dataRows
  .filter(row => row[0] !== '' && row[0] !== null && row[0] !== undefined) // Lọc dòng trống
  .map(row => {
    // Xử lý cột "Gia hạn phí" (index 2) - có thể là Date object hoặc string
    let deadline = '';
    if (row[2]) {
      if (row[2] instanceof Date) {
        // Nếu là Date object, format thành dd/mm/yyyy
        const d = row[2];
        deadline = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
      } else if (typeof row[2] === 'number') {
        // Nếu là số Excel date serial
        const excelDate = XLSX.SSF.parse_date_code(row[2]);
        deadline = `${String(excelDate.d).padStart(2, '0')}/${String(excelDate.m).padStart(2, '0')}/${excelDate.y}`;
      } else {
        // Nếu là string
        deadline = row[2].toString().trim();
      }
    }
    
const pointValue = parseFloat((row[5] || '0').toString().replace(/,/g, '').trim()) || 0;

return {
  id: parseInt(row[0]) || 0,
  name: (row[1] || '').toString().trim(),
  points: {
    [dateColumns[0]]: pointValue,
    [dateColumns[1]]: pointValue,
    [dateColumns[2]]: pointValue,
  },
  totalPoints: pointValue,  // ✅ THÊM DÒNG NÀY
  deadline: deadline,
  note: ''
};
  });

      // Đọc lịch sử giao dịch từ các sheet khác
const newTransactions = {};
workbook.SheetNames.slice(1).forEach(sheetName => {
  const sheet = workbook.Sheets[sheetName];
  const sheetData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
  const memberId = parseInt(sheetName);
  
  if (!isNaN(memberId) && sheetData.length > 1) {
    newTransactions[memberId] = sheetData.slice(3)
      .filter(row => {
        // Bỏ qua dòng header và dòng trống
        const hasDate = row[1] && row[1].toString().trim() !== '' && row[1].toString().toLowerCase() !== 'ngày';
        const hasDescription = row[2] && row[2].toString().trim() !== '' && row[2].toString().toLowerCase() !== 'nội dung' && row[2].toString().toLowerCase() !== 'diễn giải';
        const hasPrice = row[3] && row[3].toString().trim() !== '' && row[3].toString().toLowerCase() !== 'giá tiền' && !isNaN(parseFloat(row[3].toString().replace(/,/g, '')));
        return hasDate && hasDescription && hasPrice;
      })
      .map(row => ({
        date: row[1] ? (typeof row[1] === 'number' ? XLSX.SSF.format('dd/mm/yyyy', row[1]) : row[1].toString().trim()) : '',
        description: (row[2] || '').toString().trim(),
        price: parseFloat((row[3] || '0').toString().replace(/,/g, '')) || 0,
        role: (row[4] || '').toString().trim(),
        partner: (row[5] || '').toString().trim(),
        points: parseFloat((row[6] || '0').toString().replace(/,/g, '')) || 0
      }));
  }
});

      setRooms(rooms.map(room => 
        room.id === roomId 
          ? { ...room, members: newMembers, transactions: newTransactions }
          : room
      ));

      alert('Upload Excel thành công!');
    } catch (error) {
      alert('Lỗi khi đọc file Excel: ' + error.message);
    }
  };
  reader.readAsArrayBuffer(file);
};

const handleAdminLogin = async () => {
  // ✅ So sánh hash thay vì plain text
  const inputHash = await hashPassword(adminPassword);
  
  if (inputHash === ADMIN_PASSWORD_HASH) {
    try {
      // ✅ Giải mã credentials
      const { email, password } = getAdminCredentials();
      
      // ✅ BƯỚC 1: ĐĂNG NHẬP FIREBASE với credentials đã giải mã
      await signInWithEmailAndPassword(auth, email, password);
      
      
      // ✅ BƯỚC 2: KIỂM TRA session SAU (khi đã có quyền)
      const sessionCheck = await checkAndSetAdminSession(database);
      
      if (!sessionCheck.success) {
        // Nếu session check thất bại, đăng xuất Firebase
        await signOut(auth);
        alert(sessionCheck.message);
        return;
      }
      
      setIsAdminAuthenticated(true);
      setCurrentView('admin');
      alert('Đăng nhập thành công!');
      
      // ✅ BƯỚC 3: Tạo heartbeat để duy trì session
      const heartbeatInterval = setInterval(async () => {
        const mySessionId = sessionStorage.getItem('adminSessionId');
        const sessionRef = ref(database, 'adminSession');
        const snapshot = await get(sessionRef);
        const currentSession = snapshot.val();
        
        // ✅ THAY ĐỔI 4: Kiểm tra bị "đá" từ thiết bị khác
        if (currentSession && currentSession.forceLogout && currentSession.sessionId !== mySessionId) {
          clearInterval(heartbeatInterval);
          sessionStorage.removeItem('heartbeatInterval');
          alert('⚠️ Phiên đăng nhập của bạn đã bị ĐÁ từ thiết bị khác!\n\nBạn sẽ bị đăng xuất.');
          await signOut(auth);
          setCurrentView('home');
          setIsAdminAuthenticated(false);
          return;
        }
        
        // ✅ Kiểm tra session có còn là của mình không
        if (currentSession && currentSession.sessionId === mySessionId) {
          await set(sessionRef, {
            ...currentSession,
            timestamp: Date.now()
          });
          console.log('💓 Heartbeat: Session đang hoạt động');
        } else {
          clearInterval(heartbeatInterval);
          sessionStorage.removeItem('heartbeatInterval');
          alert('⚠️ Phiên đăng nhập của bạn đã hết hạn hoặc bị thay thế!');
          await signOut(auth);
          setCurrentView('home');
          setIsAdminAuthenticated(false);
        }
      }, 30000);

      sessionStorage.setItem('heartbeatInterval', heartbeatInterval);
      
    } catch (error) {
      console.error('Firebase login error:', error);
      
      if (error.code === 'auth/invalid-credential') {
        alert('Thông tin đăng nhập không đúng!');
      } else if (error.code === 'auth/network-request-failed') {
        alert('Lỗi kết nối mạng!');
      } else {
        alert('Lỗi đăng nhập: ' + error.message);
      }
    }
  } else {
    alert('Mật khẩu không đúng!');
  }
};

  const calculatePoints = (price, rule) => {
    for (let i = 0; i < rule.length; i++) {
      if (price >= rule[i].min && price < rule[i].max) {
        return rule[i].points;
      }
    }
    return null;
  };

const migratePointsToNewDay = (rooms, dateColumns) => {
  console.log('🔄 Starting migration...', { dateColumns });

  return rooms.map(room => ({
    ...room,
    members: room.members.map(member => {
      const newPoints = { ...member.points };
      const latestDate = dateColumns[2];

      const getCurrentTotal = () => {
        // Giữ nguyên số dư hiện tại kể cả khi nó bằng 0, tránh kéo lại điểm cũ.
        if (member.totalPoints !== undefined && member.totalPoints !== null) {
          return member.totalPoints;
        }

        // Nếu chưa có totalPoints thì chỉ lấy từ 2 ngày gần nhất, kể cả giá trị 0.
        if (newPoints[dateColumns[1]] !== undefined) {
          return newPoints[dateColumns[1]];
        }

        if (newPoints[dateColumns[0]] !== undefined) {
          return newPoints[dateColumns[0]];
        }

        return 0;
      };

      const currentTotal = getCurrentTotal();

      // Set ngày hôm nay
      if (newPoints[latestDate] === undefined || 
         (newPoints[latestDate] === 0 && currentTotal !== 0)) {
        console.log(`📅 Migrating ${member.name}: ${currentTotal} → ${latestDate}`);
        newPoints[latestDate] = currentTotal;
      }

      // Set 0 cho ngày cũ nếu chưa có
      if (newPoints[dateColumns[0]] === undefined) newPoints[dateColumns[0]] = 0;
      if (newPoints[dateColumns[1]] === undefined) newPoints[dateColumns[1]] = 0;

      return {
        ...member,
        points: newPoints,
        totalPoints: newPoints[latestDate]
      };
    })
  }));
};

// ✅ useEffect để migrate điểm sang ngày mới (chỉ chạy 1 lần/ngày)

useEffect(() => {
  if (rooms.length === 0 || isLoadingFromFirebase) return;

  const lastMigrationDate = localStorage.getItem('lastMigrationDate');
  const today = new Date().toDateString();
  const currentDateStr = dateColumns[2]; // Hôm nay dd/mm/yyyy

  if (lastMigrationDate !== today) {
    const needMigration = rooms.some(room =>
      room.members.some(member => {
        const val = member.points[currentDateStr];
        // Cần migrate nếu: chưa có key, HOẶC = 0 nhưng totalPoints khác 0
        return val === undefined || (val === 0 && member.totalPoints !== 0 && member.totalPoints !== undefined);
      })
    );

    if (needMigration) {
      console.log('🔄 Local migration for display...');
      const migratedRooms = migratePointsToNewDay(rooms, dateColumns);
      setRooms(migratedRooms);
      // ✅ KHÔNG lưu Firebase - GitHub Actions đã xử lý lúc 00:01
      localStorage.setItem('lastMigrationDate', today);
      console.log('✅ Local migration done');
    } else {
      localStorage.setItem('lastMigrationDate', today);
    }
  }
}, [isLoadingFromFirebase]);

const handleAddTransaction = () => {
    const { roomId, date, delivererId, receiverId, price, description, manualPoints, isAddPointTransaction, isFreeTransaction } = transactionForm;
    
    if (!roomId || !receiverId || !price || !description) {
      alert('Vui lòng điền đầy đủ thông tin!');
      return;
    }

    if (!delivererId) {
      alert('Vui lòng chọn người giao!');
      return;
    }

    if (delivererId === receiverId) {
      alert('Người giao và người nhận không thể là cùng một người!');
      return;
    }

    const priceNum = parseFloat(price);
    if (isNaN(priceNum) || priceNum <= 0) {
      alert('Giá trị không hợp lệ!');
      return;
    }

    const room = rooms.find(r => r.id === roomId);
    if (!room) return;

    let points = 0;
    
    // TÍNH ĐIỂM THEO LOẠI GIAO DỊCH
    if (isFreeTransaction) {
      points = 0;
    } else if (isAddPointTransaction) {
      if (!manualPoints) {
        alert('Vui lòng nhập số điểm cộng thủ công!');
        return;
      }
      
      // ✅ FIX: Làm tròn NGAY khi đọc từ input
      const rawPoints = parseFloat(manualPoints);
      if (isNaN(rawPoints) || rawPoints <= 0) {
        alert('Điểm thủ công không hợp lệ!');
        return;
      }
      
      // ✅ Làm tròn 1 lần duy nhất
      points = parseFloat(rawPoints.toFixed(1));
      
      console.log('🔍 Manual Points Debug:', {
        input: manualPoints,
        parsed: rawPoints,
        rounded: points
      });
      
    } else {
      // Nếu có nhập thủ công → ưu tiên dùng thủ công
      if (manualPoints) {
        const rawPoints = parseFloat(manualPoints);
        if (isNaN(rawPoints) || rawPoints <= 0) {
          alert('Điểm thủ công không hợp lệ!');
          return;
        }
        points = parseFloat(rawPoints.toFixed(1));
      } else {
        // Không có thủ công → dùng quy tắc tự động
        points = calculatePoints(priceNum, room.rule);
        
        if (points === null) {
          alert('Giá trị vượt quy tắc! Vui lòng nhập điểm thủ công.');
          return;
        }
      }
    }

    // ✅ Log kiểm tra điểm cuối cùng
    console.log('💰 Final Points:', points);

    const deliverer = room.members.find(m => m.id === parseInt(delivererId));
    const receiver = room.members.find(m => m.id === parseInt(receiverId));

    if (!deliverer || !receiver) {
      alert('Không tìm thấy thành viên!');
      return;
    }

    const dateObj = new Date(date);
    const formattedDate = `${String(dateObj.getDate()).padStart(2, '0')}/${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;

    // XÁC ĐỊNH VAI TRÒ VÀ ĐIỂM
    let delivererRole, receiverRole, delivererPoints, receiverPoints;
    
    if (isFreeTransaction) {
      delivererRole = 'Giao Free';
      receiverRole = 'Nhận Free';
      delivererPoints = 0;
      receiverPoints = 0;
    } else if (isAddPointTransaction) {
      delivererRole = 'Trừ điểm';
      receiverRole = 'Cộng điểm';
      // ✅ FIX: Không làm tròn nữa vì đã làm tròn ở trên
      delivererPoints = -points;
      receiverPoints = points;
    } else {
      delivererRole = 'Giao';
      receiverRole = 'Nhận';
      delivererPoints = points;
      receiverPoints = -points;
    }

    console.log('📊 Transaction Points:', {
      delivererPoints,
      receiverPoints
    });

    const delivererTransaction = {
      date: formattedDate,
      description: description,
      price: priceNum,
      role: delivererRole,
      partner: receiver.name,
      points: delivererPoints
    };

    const receiverTransaction = {
      date: formattedDate,
      description: description,
      price: priceNum,
      role: receiverRole,
      partner: deliverer.name,
      points: receiverPoints
    };

    setRooms(rooms.map(r => {
      if (r.id !== roomId) return r;

      const newTransactions = { ...r.transactions };
      if (!newTransactions[deliverer.id]) newTransactions[deliverer.id] = [];
      if (!newTransactions[receiver.id]) newTransactions[receiver.id] = [];
      
      newTransactions[deliverer.id] = [...newTransactions[deliverer.id], delivererTransaction];
      newTransactions[receiver.id] = [...newTransactions[receiver.id], receiverTransaction];

      const currentDate = dateColumns[2];
      const newMembers = r.members.map(m => {
        if (!m.points[currentDate]) {
          m.points[currentDate] = m.totalPoints || 0;
        }
        if (m.totalPoints === undefined) {
          m.totalPoints = m.points[currentDate] || 0;
        }
        
        if (m.id === deliverer.id && !isFreeTransaction) {
          // ✅ FIX: Làm tròn sau khi cộng
          const newTotal = parseFloat((m.totalPoints + delivererPoints).toFixed(1));
          
          console.log(`✅ ${m.name} (Deliverer):`, {
            oldTotal: m.totalPoints,
            change: delivererPoints,
            newTotal
          });
          
          return {
            ...m,
            points: {
              ...m.points,
              [currentDate]: newTotal
            },
            totalPoints: newTotal
          };
        }
        if (m.id === receiver.id && !isFreeTransaction) {
          // ✅ FIX: Làm tròn sau khi cộng
          const newTotal = parseFloat((m.totalPoints + receiverPoints).toFixed(1));
          
          console.log(`✅ ${m.name} (Receiver):`, {
            oldTotal: m.totalPoints,
            change: receiverPoints,
            newTotal
          });
          
          return {
            ...m,
            points: {
              ...m.points,
              [currentDate]: newTotal
            },
            totalPoints: newTotal
          };
        }
        return m;
      });

      return {
        ...r,
        transactions: newTransactions,
        members: newMembers
      };
    }));

    // Reset form...
    setTransactionForm({
      roomId: null,
      date: new Date().toISOString().split('T')[0],
      delivererId: '',
      receiverId: '',
      price: '',
      description: '',
      manualPoints: '',
      isAddPointTransaction: false,
      isFreeTransaction: false
    });
    setShowTransactionForm(false);
    setDelivererSearch('');
    setReceiverSearch('');
    setShowDelivererDropdown(false);
    setShowReceiverDropdown(false);
    
    let successMsg;
    if (isFreeTransaction) {
      successMsg = `Đã thêm giao dịch Free (0 điểm) cho ${receiver.name}!`;
    } else if (isAddPointTransaction) {
      successMsg = `Đã cộng ${points} điểm cho ${receiver.name}, trừ ${points} điểm từ ${deliverer.name}!`;
    } else {
      successMsg = 'Thêm giao dịch thành công!';
    }
    
    alert(successMsg);
};
  const handleAddMember = () => {
    const { roomId, id, name, deadline, note } = memberForm;
  
    if (!roomId || id === '' || !name) {
    alert('Vui lòng điền đầy đủ thông tin bắt buộc (Room, ID, Tên)!');
    return;
    }

    const memberId = parseInt(id);
    if (isNaN(memberId)) {
    alert('ID phải là số!');
    return;
    }

    const room = rooms.find(r => r.id === roomId);
    if (!room) return;

    if (room.members.some(m => m.id === memberId)) {
    alert('ID này đã tồn tại trong Room! Vui lòng chọn ID khác.');
    return;
    }

    const newMember = {
    id: memberId,
    name: name.trim(),
    points: {
      [dateColumns[0]]: 0,
      [dateColumns[1]]: 0,
      [dateColumns[2]]: 0
    },
    totalPoints: 0,
    deadline: deadline || '',
    note: note || ''
    };

    setRooms(rooms.map(r => {
    if (r.id !== roomId) return r;
    return {
      ...r,
      members: [...r.members, newMember],
      transactions: {
        ...r.transactions,
        [memberId]: []
      }
    };
  }));

  setMemberForm({ roomId: null, id: '', name: '', deadline: '', note: '', initialPoints: '0' });
  setShowMemberForm(false);
  alert(`Đã thêm thành viên "${name}" thành công!\n\n💡 Dùng "Giao dịch cộng điểm" (Admin → ${name}) để cấp điểm ban đầu.`);
  };

  const handleCreateRoom = () => {
    const { name, icon, password, rule } = roomForm;
    
    if (!name.trim()) {
      alert('Vui lòng nhập tên Room!');
      return;
    }

    const newRoom = {
    id: rooms.length > 0 ? Math.max(...rooms.map(r => r.id)) + 1 : 1,
    name: name.trim(),
    icon: icon || '🏠',
    qrCode: null,
    password: password.trim(),
    rule: rule,
    members: [],
    transactions: {}
    };

    setRooms([...rooms, newRoom]);
    setRoomForm({
    name: '',
    icon: '🏠',
    password: '',
    rule: [
        { min: 500000, max: 1000000, points: 0.5 },
        { min: 1000000, max: 2000000, points: 1 },
        { min: 2000000, max: 5000000, points: 2 },
        { min: 5000000, max: 10000000, points: 3 },
      ]
    });
    setShowRoomForm(false);
    
    alert(`Đã tạo Room "${name}" thành công!`);
  };

  const handleEditRoom = (room) => {
    setEditingRoom(room);
    setRoomForm({
    name: room.name,
    icon: room.icon,
    password: room.password || '',
    rule: [...room.rule]
  });
    setShowRoomForm(true);
  };

  const handleUpdateRoom = () => {
    const { name, icon, password, rule } = roomForm;
    
    if (!name.trim()) {
      alert('Vui lòng nhập tên Room!');
      return;
    }

    setRooms(rooms.map(r => 
        r.id === editingRoom.id 
          ? { ...r, name: name.trim(), icon: icon, password: password.trim(), rule: rule }
          : r
    ));

    setRoomForm({
      name: '',
      icon: '🏠',
      password: '',
      rule: [
        { min: 500000, max: 1000000, points: 0.5 },
        { min: 1000000, max: 2000000, points: 1 },
        { min: 2000000, max: 5000000, points: 2 },
        { min: 5000000, max: 10000000, points: 3 },
      ]
    });
    setShowRoomForm(false);
    setEditingRoom(null);
    
    alert(`Đã cập nhật Room "${name}" thành công!`);
  };

  const handleDeleteRoom = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    if (!room) return;

    const confirm = window.confirm(`Bạn có chắc chắn muốn xóa Room "${room.name}"?\n\nSẽ xóa tất cả thành viên và giao dịch trong Room này!`);
    if (!confirm) return;

    setRooms(rooms.filter(r => r.id !== roomId));
    alert(`Đã xóa Room "${room.name}" thành công!`);
  };

  const handleEditMember = (member, roomId) => {
  setEditingMember({ ...member, roomId });
  setMemberForm({
    roomId: roomId,
    id: member.id.toString(),
    name: member.name,
    deadline: member.deadline,
    note: member.note,
    initialPoints: '0' // không dùng nhưng giữ để tránh lỗi
  });
  setShowMemberForm(true);
  };

  const handleUpdateMember = () => {
  const { roomId, name, deadline, note } = memberForm;
  
  if (!name) {
    alert('Vui lòng nhập tên thành viên!');
    return;
  }

  setRooms(rooms.map(r => {
    if (r.id !== roomId) return r;
    return {
      ...r,
      members: r.members.map(m => 
        m.id === editingMember.id
          ? {
              ...m,
              name: name.trim(),
              // ✅ Không chạm vào points/totalPoints
              deadline: deadline || '',
              note: note || ''
            }
          : m
      )
    };
  }));

  setMemberForm({ roomId: null, id: '', name: '', deadline: '', note: '', initialPoints: '0' });
  setShowMemberForm(false);
  setEditingMember(null);
  alert(`Đã cập nhật thông tin thành viên "${name}" thành công!`);
  };

  const handleDeleteMember = (memberId, roomId) => {
    const room = rooms.find(r => r.id === roomId);
    const member = room?.members.find(m => m.id === memberId);
    if (!member) return;

    setShowDeleteMemberConfirm({ memberId, roomId, memberName: member.name });
  };

  const confirmDeleteMember = () => {
    const { memberId, roomId, memberName } = showDeleteMemberConfirm;

    setRooms(rooms.map(r => {
      if (r.id !== roomId) return r;
      
      const newTransactions = { ...r.transactions };
      delete newTransactions[memberId];
      
      return {
        ...r,
        members: r.members.filter(m => m.id !== memberId),
        transactions: newTransactions
      };
    }));

    setShowDeleteMemberConfirm(null);
    alert(`Đã xóa thành viên "${memberName}" thành công!`);
  };

  const handleViewAllTransactions = (room) => {
    setSelectedRoomTransactions(room);
    setShowAllTransactions(true);
  };

const getAllTransactionsFlat = (room) => {
  const allTransactions = [];
  
  Object.entries(room.transactions).forEach(([memberId, transactions]) => {
    const member = room.members.find(m => m.id === parseInt(memberId));
    transactions.forEach((trans, originalIndex) => {
      allTransactions.push({
        ...trans,
        memberId: parseInt(memberId),
        memberName: member?.name || `ID: ${memberId}`,
        originalIndex  // ✅ THÊM: Lưu index gốc để giữ thứ tự nhập
      });
    });
  });
  
  // ✅ Nhóm các giao dịch thành cặp (GIỮ NGUYÊN THỨ TỰ NHẬP)
  const groupedTransactions = [];
  const usedIndices = new Set();
  
  allTransactions.forEach((trans, index) => {
    if (usedIndices.has(index)) return;
    
    // Tìm giao dịch cặp đôi
    const pairIndex = allTransactions.findIndex((t, i) => {
      if (i <= index || usedIndices.has(i)) return false;
      
      const isSameTransaction = 
        t.date === trans.date &&
        t.description === trans.description &&
        Math.abs(t.price - trans.price) < 0.01 &&
        t.memberName === trans.partner &&
        t.partner === trans.memberName;
      
      return isSameTransaction;
    });
    
    if (pairIndex !== -1) {
      const pair = allTransactions[pairIndex];
      
      // ✅ Sắp xếp cặp: Giao/Cộng điểm/Giao Free/Trừ điểm trước
      if (trans.role === 'Giao' || trans.role === 'Cộng điểm' || trans.role === 'Giao Free' || trans.role === 'Trừ điểm') {
        groupedTransactions.push({
          pair: [trans, pair],
          date: trans.date,
          timestamp: trans.originalIndex  // ✅ Dùng index gốc làm timestamp
        });
      } else {
        groupedTransactions.push({
          pair: [pair, trans],
          date: pair.date,
          timestamp: pair.originalIndex  // ✅ Dùng index gốc làm timestamp
        });
      }
      
      usedIndices.add(index);
      usedIndices.add(pairIndex);
    } else {
      // Giao dịch đơn lẻ
      groupedTransactions.push({
        pair: [trans],
        date: trans.date,
        timestamp: trans.originalIndex
      });
      usedIndices.add(index);
    }
  });
  
  // ✅ Sort theo NGÀY (mới nhất trước), SAU ĐÓ theo TIMESTAMP (mới nhất trước)
  groupedTransactions.sort((a, b) => {
    const dateCompare = parseTransactionDate(b.date) - parseTransactionDate(a.date);
    
    // Nếu cùng ngày, so sánh timestamp (index cao hơn = nhập sau = hiển thị trước)
    if (dateCompare === 0) {
      return b.timestamp - a.timestamp;
    }
    
    return dateCompare;
  });
  
  // ✅ Giải nén các cặp ra thành mảng phẳng
  const result = [];
  groupedTransactions.forEach(group => {
    result.push(...group.pair);
  });
  
  return result;
};
  const handleEditTransaction = (transaction, room) => {
    setEditingTransaction({ ...transaction, roomId: room.id });
    
    const deliverer = room.members.find(m => m.name === (transaction.role === 'Giao' ? transaction.memberName : transaction.partner));
    const receiver = room.members.find(m => m.name === (transaction.role === 'Nhận' ? transaction.memberName : transaction.partner));
    
    setTransactionForm({
      roomId: room.id,
      date: new Date().toISOString().split('T')[0],
      delivererId: deliverer?.id.toString() || '',
      receiverId: receiver?.id.toString() || '',
      price: transaction.price.toString(),
      description: transaction.description,
      manualPoints: ''
    });
    setShowAllTransactions(false);
    setShowTransactionForm(true);
  };

const handleDeleteTransaction = (transaction, room) => {
  const confirm = window.confirm(`Bạn có chắc chắn muốn xóa giao dịch này?\n\nNgày: ${transaction.date}\nGiá trị: ${transaction.price.toLocaleString('vi-VN')} VND`);
  if (!confirm) return;

  setRooms(rooms.map(r => {
    if (r.id !== room.id) return r;

    // Bước 1: Tìm deliverer và receiver
    let delivererName, receiverName;
    
    if (transaction.role === 'Giao' || transaction.role === 'Giao Free' || transaction.role === 'Trừ điểm') {
      delivererName = transaction.memberName;
      receiverName = transaction.partner;
    } else {
      delivererName = transaction.partner;
      receiverName = transaction.memberName;
    }

    const deliverer = room.members.find(m => m.name === delivererName);
    const receiver = room.members.find(m => m.name === receiverName);

    if (!deliverer || !receiver) {
      console.error('❌ Cannot find members:', { delivererName, receiverName });
      return r;
    }

    console.log('✅ Found members:', { 
      deliverer: deliverer.name, 
      receiver: receiver.name 
    });

    // Bước 2: Lấy danh sách giao dịch
    const delivererTransactions = r.transactions[deliverer.id] || [];
    const receiverTransactions = r.transactions[receiver.id] || [];

    console.log('📋 Before delete:', {
      delivererCount: delivererTransactions.length,
      receiverCount: receiverTransactions.length
    });

    // Bước 3: Tìm giao dịch chính xác
    const delivererTrans = delivererTransactions.find(t => 
      t.date === transaction.date && 
      Math.abs(t.price - transaction.price) < 0.01 && // So sánh số thực
      t.description === transaction.description &&
      t.partner === receiverName
    );

    const receiverTrans = receiverTransactions.find(t => 
      t.date === transaction.date && 
      Math.abs(t.price - transaction.price) < 0.01 && // So sánh số thực
      t.description === transaction.description &&
      t.partner === delivererName
    );

    if (!delivererTrans || !receiverTrans) {
      console.error('❌ Cannot find matching transactions');
      return r;
    }

    console.log('🔍 Found transactions to delete:', {
      deliverer: delivererTrans,
      receiver: receiverTrans
    });

    // Bước 4: XÓA giao dịch từ cả 2 phía
    const newTransactions = { ...r.transactions };
    
    newTransactions[deliverer.id] = delivererTransactions.filter(t => 
      !(
        t.date === delivererTrans.date && 
        Math.abs(t.price - delivererTrans.price) < 0.01 &&
        t.description === delivererTrans.description && 
        t.partner === delivererTrans.partner
      )
    );
    
    newTransactions[receiver.id] = receiverTransactions.filter(t => 
      !(
        t.date === receiverTrans.date && 
        Math.abs(t.price - receiverTrans.price) < 0.01 &&
        t.description === receiverTrans.description && 
        t.partner === receiverTrans.partner
      )
    );

    console.log('🗑️ After delete:', {
      delivererCount: newTransactions[deliverer.id].length,
      receiverCount: newTransactions[receiver.id].length
    });

    // Bước 5: HOÁN NGƯỢC điểm
    const currentDate = dateColumns[2];
    const isFreeTransaction = delivererTrans.role === 'Giao Free' || receiverTrans.role === 'Nhận Free';

    const newMembers = r.members.map(m => {
      // Khởi tạo totalPoints nếu chưa có
      if (m.totalPoints === undefined) {
        m.totalPoints = m.points[currentDate] || 0;
      }
      
      if (isFreeTransaction) {
        return m;
      }
      
      if (m.id === deliverer.id) {
        const pointsToRevert = -delivererTrans.points;
        const newTotal = Math.round((m.totalPoints + pointsToRevert) * 10) / 10;
        
        console.log(`🔄 ${m.name}:`, {
          oldTotal: m.totalPoints,
          pointsToRevert,
          newTotal
        });
        
        return {
          ...m,
          points: {
            ...m.points,
            [currentDate]: newTotal
          },
          totalPoints: newTotal
        };
      }
      
      if (m.id === receiver.id) {
        const pointsToRevert = -receiverTrans.points;
        const newTotal = Math.round((m.totalPoints + pointsToRevert) * 10) / 10;
        
        console.log(`🔄 ${m.name}:`, {
          oldTotal: m.totalPoints,
          pointsToRevert,
          newTotal
        });
        
        return {
          ...m,
          points: {
            ...m.points,
            [currentDate]: newTotal
          },
          totalPoints: newTotal
        };
      }
      
      return m;
    });

    return {
      ...r,
      transactions: newTransactions,
      members: newMembers
    };
  }));

  alert('Đã xóa giao dịch thành công!');
  
  if (selectedRoomTransactions) {
    const updatedRoom = rooms.find(r => r.id === room.id);
    if (updatedRoom) {
      setSelectedRoomTransactions(updatedRoom);
    }
  }
};

  const handleUploadQR = async (event, roomId) => {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = async (e) => {
    const qrCodeData = e.target.result;
    
    // Update state
    const updatedRooms = rooms.map(r => 
      r.id === roomId 
        ? { ...r, qrCode: qrCodeData }
        : r
    );
    setRooms(updatedRooms);
    
    // Lưu trực tiếp lên Firebase
    try {
      const roomsRef = ref(database, 'rooms');
      const firebaseData = convertToFirebase(updatedRooms);
      await set(roomsRef, firebaseData);
      console.log('✅ QR Code saved to Firebase');
      alert('Upload QR Code thành công!');
    } catch (error) {
      console.error('❌ Error saving QR to Firebase:', error);
      alert('Lỗi khi lưu QR Code: ' + error.message);
    }
    
    setShowQRUpload(null);
  };
  reader.readAsDataURL(file);
};

  const handleRemoveQR = (roomId) => {
    const confirm = window.confirm('Bạn có chắc chắn muốn xóa QR Code?');
    if (!confirm) return;

    setRooms(rooms.map(r => 
      r.id === roomId 
        ? { ...r, qrCode: null }
        : r
    ));
    alert('Đã xóa QR Code!');
    setShowQRUpload(null);
  };

  const handleExportData = () => {
    const dataStr = JSON.stringify(rooms, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `room-data-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    alert('Đã xuất dữ liệu thành công!');
  };

  const handleImportData = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        const confirm = window.confirm('Bạn có chắc chắn muốn import dữ liệu?\n\nDữ liệu hiện tại sẽ bị ghi đè!');
        if (!confirm) return;

        setRooms(importedData);
        alert('Import dữ liệu thành công!');
      } catch (error) {
        alert('Lỗi khi đọc file: ' + error.message);
      }
    };
    reader.readAsText(file);
  };

  const handleClearAllData = () => {
    const confirm = window.confirm('⚠️ CẢNH BÁO!\n\nBạn có chắc chắn muốn XÓA TẤT CẢ dữ liệu?\n\nHành động này KHÔNG THỂ HOÀN TÁC!');
    if (!confirm) return;

    const confirmAgain = window.confirm('Xác nhận lần cuối: XÓA TẤT CẢ?');
    if (!confirmAgain) return;

    localStorage.removeItem('roomManagementData');
    setRooms(initialRooms);
    alert('Đã xóa tất cả dữ liệu và reset về mặc định!');
  };

    const handleExportRoomToExcel = (room) => {
      const wb = XLSX.utils.book_new();

      // Sheet 1: Tổng hợp thành viên (format giống file import)
      const summaryData = room.members.map(member => ({
        'STT': member.id,
        'Tên Thành Viên': member.name,
        'Gia hạn phí': member.deadline || '',
        'Quỹ': '', // Cột trống
        'Điểm mua': '', // Cột trống
        'Điểm Tồn': member.totalPoints || member.points[dateColumns[2]] || 0
      }));
      const ws1 = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, ws1, 'DS'); // ĐỔI TÊN SHEET từ 'Tổng Hợp' sang 'DS'

    // Sheet 2+: Lịch sử từng thành viên (tên sheet = ID)
    room.members.forEach(member => {
      const transactions = room.transactions[member.id] || [];
      if (transactions.length > 0) {
        const transData = transactions.map(trans => ({
          'Ngày': trans.date,
          'Diễn Giải': trans.description,
          'Giá Tiền': trans.price,
          'Vai Trò (Giao/Nhận)': trans.role,
          'Đối Tác': trans.partner,
          'Điểm (+/-)': trans.points
        }));
        const ws = XLSX.utils.json_to_sheet(transData);
        XLSX.utils.book_append_sheet(wb, ws, member.id.toString());
      }
    });

    // Xuất file
    const fileName = `${room.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    alert(`Đã xuất Excel cho Room "${room.name}" thành công!`);
  };

  const handleExportAllRoomsToExcel = () => {
    const wb = XLSX.utils.book_new();

    rooms.forEach((room, index) => {
      const summaryData = room.members.map(member => ({
        'STT': member.id,
        'Tên Thành Viên': member.name,
        'Gia hạn phí': member.deadline || '',
        'Quỹ': '',
        'Điểm mua': '',
        'Điểm Tồn': member.totalPoints || member.points[dateColumns[2]] || 0
      }));
      
      const ws = XLSX.utils.json_to_sheet(summaryData);
      const sheetName = `Room${index + 1}_${room.name.substring(0, 20)}`.replace(/[^a-zA-Z0-9]/g, '_');
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    const fileName = `TatCaRoom_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    alert('Đã xuất Excel cho tất cả Rooms thành công!');
  };

  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();

    // Template Sheet 1: Tổng hợp
    const templateSummary = [
      {
        'ID': 0,
        'Tên Thành Viên': 'Nguyễn Văn A',
        '02/10': 0,
        '03/10': 0,
        '04/10': 0,
        'Hạn thu KT': 'Tháng 12/2025',
        'Ghi chú': 'RET1'
      },
      {
        'ID': 1,
        'Tên Thành Viên': 'Trần Thị B',
        '02/10': 5.5,
        '03/10': 5.5,
        '04/10': 5.5,
        'Hạn thu KT': 'Tháng 01/2026',
        'Ghi chú': 'Cọc'
      }
    ];
    const ws1 = XLSX.utils.json_to_sheet(templateSummary);
    XLSX.utils.book_append_sheet(wb, ws1, 'Tổng Hợp');

    // Template Sheet 2: Lịch sử giao dịch (ID = 0)
    const templateTransactions = [
      {
        'Ngày': '24/09',
        'Diễn Giải': 'Giao hàng cho khách hàng A',
        'Giá Tiền': 8500000,
        'Vai Trò (Giao/Nhận)': 'Giao',
        'Đối Tác': 'Trần Thị B',
        'Điểm (+/-)': 3
      },
      {
        'Ngày': '25/09',
        'Diễn Giải': 'Nhận hàng từ kho',
        'Giá Tiền': 2400000,
        'Vai Trò (Giao/Nhận)': 'Nhận',
        'Đối Tác': 'Nguyễn Văn C',
        'Điểm (+/-)': -2
      }
    ];
    const ws2 = XLSX.utils.json_to_sheet(templateTransactions);
    XLSX.utils.book_append_sheet(wb, ws2, '0');

    // Template Sheet 3: Lịch sử giao dịch (ID = 1)
    const ws3 = XLSX.utils.json_to_sheet(templateTransactions);
    XLSX.utils.book_append_sheet(wb, ws3, '1');

    XLSX.writeFile(wb, 'Template_Room.xlsx');
    alert('Đã tải Template Excel mẫu!\n\nBạn có thể dùng file này để tạo Room mới.');
  };


  if (currentView === 'home') {
  // Hiển thị loading khi đang tải dữ liệu
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Đang tải dữ liệu...</p>
        </div>
      </div>
    );
  }
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 relative">
            {/* Nút Admin ẩn - nhấn 3 lần liên tiếp */}
            <button
              onClick={() => {
                const now = Date.now();
                const clicks = JSON.parse(sessionStorage.getItem('adminClicks') || '[]');
                clicks.push(now);
                
                // Chỉ giữ các click trong 2 giây gần nhất
                const recentClicks = clicks.filter(time => now - time < 2000);
                sessionStorage.setItem('adminClicks', JSON.stringify(recentClicks));
                
                if (recentClicks.length >= 3) {
                  sessionStorage.removeItem('adminClicks');
                  setCurrentView('adminLogin');
                }
              }}
              className="absolute top-0 right-0 w-10 h-10 opacity-0"
              title=""
            >
            </button>

            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
              Các thành viên bấm vào logo từng room để xem điểm
            </h1>
            <p className="text-gray-600 italic mb-2">
              Lưu ý nhìn kỹ ngày, tổng kết đến 23h59 hàng ngày.
            </p>
            <p className="text-red-600 font-semibold">
              Ngày hôm nay: {currentDate}
            </p>
          </div>



          <div className="flex flex-wrap justify-center gap-6 max-w-5xl mx-auto">
            {rooms.map(room => (
              <div
                key={room.id}
                onClick={() => handleRoomClick(room)}
                className="flex flex-col items-center cursor-pointer hover:scale-105 transition-transform"
              >
                <div className="w-32 h-32 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-lg mb-3">
                  <span className="text-6xl">{room.icon}</span>
                </div>
                <h3 className="text-center font-semibold text-gray-800 max-w-[180px]">{room.name}</h3>
              </div>
            ))}
          </div>

<div className="text-center mt-8">
  <p className="text-gray-600 text-sm">
    👁️ Lượt truy cập: <span className="font-semibold text-blue-600">{visitCount.toLocaleString('vi-VN')}</span>

  </p>
</div>
{/* QR Code góc dưới bên phải */}
{rooms.find(room => room.qrCode) && (
  <div className="fixed bottom-4 right-4 bg-white p-3 rounded-lg shadow-xl border-2 border-blue-500 z-40">
    <img 
      src={rooms.find(room => room.qrCode).qrCode} 
      alt="QR Code Zalo" 
      className="w-32 h-32 object-contain"
    />
    <p className="text-center text-xs text-gray-600 mt-1">QR Zalo</p>
  </div>
)}

{/* Placeholder nếu chưa có QR */}
{!rooms.find(room => room.qrCode) && (
  <div className="fixed bottom-4 right-4 bg-white p-2 rounded-lg shadow-lg z-40">
    <div className="w-24 h-24 bg-gray-200 flex items-center justify-center text-xs text-gray-500">
      QR Zalo
    </div>
  </div>
  )}
{/* THÊM MODAL NHẬP PASSWORD */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-4xl">{showPasswordModal.icon}</span>
                <div>
                  <h3 className="text-xl font-bold text-gray-800">{showPasswordModal.name}</h3>
                  <p className="text-sm text-gray-600">Room này được bảo vệ bằng mật khẩu</p>
                </div>
              </div>
              
              <input
                type="password"
                placeholder="Nhập mật khẩu..."
                value={passwordInput}
                onChange={(e) => {
                  setPasswordInput(e.target.value);
                  setPasswordError('');
                }}
                onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                className={`w-full px-4 py-3 border rounded-lg mb-2 focus:outline-none focus:ring-2 ${
                  passwordError ? 'border-red-500 focus:ring-red-500' : 'focus:ring-blue-500'
                }`}
                autoFocus
              />
              
              {passwordError && (
                <p className="text-red-600 text-sm mb-3">⚠️ {passwordError}</p>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={handlePasswordSubmit}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-semibold"
                >
                  Xác nhận
                </button>
                <button
                  onClick={() => {
                    setShowPasswordModal(null);
                    setPasswordInput('');
                    setPasswordError('');
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition font-semibold"
                >
                  Hủy
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>  
    );
  }

  if (currentView === 'adminLogin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Đăng nhập Admin</h2>
          <input
            type="password"
            placeholder="Nhập mật khẩu..."
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin()}
            className="w-full px-4 py-3 border rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleAdminLogin}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition mb-3"
          >
            Đăng nhập
          </button>
          <button
            onClick={() => setCurrentView('home')}
            className="w-full bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition"
          >
            Quay lại
          </button>
        </div>
      </div>
    );
  }

  if (currentView === 'admin') {
    return (
      <>
        <div className="min-h-screen bg-gray-50 p-4 md:p-8">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-6">
<div className="mb-6">
  {/* Title */}
  <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">Quản lý Admin</h1>
  
  {/* ✅ GOM NHÓM CÁC BUTTON VÀO DROPDOWN TRÊN MOBILE */}
  <div className="mb-4">
    {/* Desktop: Hiển thị đầy đủ các button */}
    <div className="hidden md:grid md:grid-cols-3 lg:flex lg:flex-wrap gap-2">
      <button
        onClick={handleDownloadTemplate}
        className="flex items-center justify-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 text-sm"
        title="Tải Template Excel"
      >
        <Download size={18} />
        Template
      </button>
      
      <button
        onClick={handleExportAllRoomsToExcel}
        className="flex items-center justify-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 text-sm"
        title="Xuất tất cả Rooms"
      >
        <FileJson size={18} />
        Xuất Excel
      </button>
      
      <button
        onClick={handleExportData}
        className="flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm"
        title="Xuất dữ liệu JSON"
      >
        <Download size={18} />
        Xuất JSON
      </button>
      
      <label className="flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 cursor-pointer text-sm">
        <Upload size={18} />
        Nhập JSON
        <input
          type="file"
          accept=".json"
          onChange={handleImportData}
          className="hidden"
        />
      </label>
      
      <button
        onClick={() => {
          setCurrentView('home');
          setIsAdminAuthenticated(false);
          setAdminPassword('');
        }}
        className="flex items-center justify-center gap-2 bg-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300 text-sm"
      >
        <Home size={18} />
        Trang chủ
      </button>
      
      <button
        onClick={async () => {
          const roomsRef = ref(database, 'rooms');
          const snapshot = await get(roomsRef);
          const data = snapshot.val();
          if (data && Array.isArray(data)) {
            const converted = convertFromFirebase(data);
            setRooms(converted);
            alert('Đã tải lại dữ liệu từ Firebase!');
          }
        }}
        className="flex items-center justify-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 text-sm"
      >
        🔄
        Tải lại
      </button>
      
      <button
        onClick={async () => {
          const confirmLogout = window.confirm('Bạn có chắc chắn muốn đăng xuất?');
          if (!confirmLogout) return;
          
          try {
            const mySessionId = sessionStorage.getItem('adminSessionId');
            const sessionRef = ref(database, 'adminSession');
            const snapshot = await get(sessionRef);
            const currentSession = snapshot.val();
            
            if (currentSession && currentSession.sessionId === mySessionId) {
              await set(sessionRef, null);
              console.log('🗑️ Deleted session from Firebase');
            }
            
            const heartbeatInterval = sessionStorage.getItem('heartbeatInterval');
            if (heartbeatInterval) {
              clearInterval(parseInt(heartbeatInterval));
              sessionStorage.removeItem('heartbeatInterval');
              console.log('⏹️ Stopped heartbeat');
            }
            
            sessionStorage.removeItem('adminSessionId');
            setIsAdminAuthenticated(false);
            setAdminPassword('');
            await signOut(auth);
            setCurrentView('home');
            
            alert('✅ Đã đăng xuất thành công!');
          } catch (error) {
            console.error('Logout error:', error);
            
            if (error.code === 'PERMISSION_DENIED' || error.message.includes('Permission denied')) {
              const heartbeatInterval = sessionStorage.getItem('heartbeatInterval');
              if (heartbeatInterval) {
                clearInterval(parseInt(heartbeatInterval));
                sessionStorage.removeItem('heartbeatInterval');
              }
              sessionStorage.removeItem('adminSessionId');
              
              setIsAdminAuthenticated(false);
              setAdminPassword('');
              setCurrentView('home');
              
              try {
                await signOut(auth);
              } catch (signOutError) {
                console.error('SignOut error:', signOutError);
              }
              
              alert('⚠️ Đã đăng xuất thành công!\n\n(Không thể xóa session trên server, nhưng bạn đã đăng xuất khỏi thiết bị này)');
            } else {
              alert('Lỗi khi đăng xuất: ' + error.message);
            }
          }
        }}
        className="flex items-center justify-center gap-2 bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 text-sm"
      >
        Đăng xuất
      </button>
    </div>

    {/* ✅ MOBILE: Menu dropdown gọn gàng */}
    <div className="md:hidden">
      <button
        onClick={() => setShowAdminMenu(!showAdminMenu)}
        className="w-full flex items-center justify-between bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 font-semibold text-sm shadow-md"
      >
        <span className="flex items-center gap-2">
          <Settings size={18} />
          Công cụ quản lý
        </span>
        <span className={`transform transition-transform ${showAdminMenu ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>

      {/* Dropdown menu */}
      {showAdminMenu && (
        <div className="mt-2 bg-white border rounded-lg shadow-lg overflow-hidden">
          <button
            onClick={handleDownloadTemplate}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left text-sm border-b"
          >
            <Download size={18} className="text-indigo-600" />
            <span>Tải Template Excel</span>
          </button>
          
          <button
            onClick={handleExportAllRoomsToExcel}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left text-sm border-b"
          >
            <FileJson size={18} className="text-purple-600" />
            <span>Xuất tất cả Excel</span>
          </button>
          
          <button
            onClick={handleExportData}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left text-sm border-b"
          >
            <Download size={18} className="text-green-600" />
            <span>Xuất dữ liệu JSON</span>
          </button>
          
          <label className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left text-sm border-b cursor-pointer">
            <Upload size={18} className="text-blue-600" />
            <span>Nhập dữ liệu JSON</span>
            <input
              type="file"
              accept=".json"
              onChange={handleImportData}
              className="hidden"
            />
          </label>
          
          <button
            onClick={async () => {
              const roomsRef = ref(database, 'rooms');
              const snapshot = await get(roomsRef);
              const data = snapshot.val();
              if (data && Array.isArray(data)) {
                const converted = convertFromFirebase(data);
                setRooms(converted);
                alert('Đã tải lại dữ liệu từ Firebase!');
              }
              setShowAdminMenu(false);
            }}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left text-sm border-b"
          >
            <span className="text-orange-600">🔄</span>
            <span>Tải lại dữ liệu</span>
          </button>
          
          <button
            onClick={() => {
              setCurrentView('home');
              setIsAdminAuthenticated(false);
              setAdminPassword('');
              setShowAdminMenu(false);
            }}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left text-sm border-b"
          >
            <Home size={18} className="text-gray-600" />
            <span>Về trang chủ</span>
          </button>
          
          <button
            onClick={async () => {
              const confirmLogout = window.confirm('Bạn có chắc chắn muốn đăng xuất?');
              if (!confirmLogout) return;
              
              try {
                const mySessionId = sessionStorage.getItem('adminSessionId');
                const sessionRef = ref(database, 'adminSession');
                const snapshot = await get(sessionRef);
                const currentSession = snapshot.val();
                
                if (currentSession && currentSession.sessionId === mySessionId) {
                  await set(sessionRef, null);
                }
                
                const heartbeatInterval = sessionStorage.getItem('heartbeatInterval');
                if (heartbeatInterval) {
                  clearInterval(parseInt(heartbeatInterval));
                  sessionStorage.removeItem('heartbeatInterval');
                }
                
                sessionStorage.removeItem('adminSessionId');
                setIsAdminAuthenticated(false);
                setAdminPassword('');
                await signOut(auth);
                setCurrentView('home');
                setShowAdminMenu(false);
                
                alert('✅ Đã đăng xuất thành công!');
              } catch (error) {
                console.error('Logout error:', error);
                alert('Lỗi khi đăng xuất: ' + error.message);
              }
            }}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 text-left text-sm text-red-600 font-semibold"
          >
            <span>🚪</span>
            <span>Đăng xuất</span>
          </button>
        </div>
      )}
    </div>
  </div>
</div>
<div className="space-y-6">
  <div className="bg-blue-50 border border-blue-200 rounded-lg overflow-hidden">
  {/* Header - Click để toggle */}
  <button
    onClick={() => setShowSystemStatus(!showSystemStatus)}
    className="w-full flex items-center justify-between p-4 hover:bg-blue-100 transition"
  >
    <div className="flex items-center gap-2">
      <FileJson size={20} className="text-blue-600" />
      <h3 className="font-semibold text-blue-900">Trạng thái hệ thống</h3>
    </div>
    <span className={`transform transition-transform text-blue-600 ${showSystemStatus ? 'rotate-180' : ''}`}>
      ▼
    </span>
  </button>

  {/* Nội dung - Hiển thị khi showSystemStatus = true */}
  {showSystemStatus && (
    <div className="px-4 pb-4 space-y-2 border-t border-blue-200 pt-3">
      <p className="text-sm text-blue-800">
        🔐 Firebase Auth: <span className={`font-semibold ${isFirebaseAuthenticated ? 'text-green-600' : 'text-red-600'}`}>
          {isFirebaseAuthenticated ? 'Đã đăng nhập' : 'Chưa đăng nhập'}
        </span>
      </p>
      <p className="text-sm text-blue-800">
        👤 Admin Session: <span className={`font-semibold ${isAdminAuthenticated ? 'text-green-600' : 'text-red-600'}`}>
          {isAdminAuthenticated ? 'Đang hoạt động' : 'Không hoạt động'}
        </span>
      </p>
      <p className="text-sm text-blue-800">
        💾 Tổng số Room: <span className="font-semibold">{rooms.length}</span>
      </p>
      {isAdminAuthenticated && sessionStorage.getItem('adminSessionId') && (
        <p className="text-xs text-blue-600 mt-2">
          🔑 Session ID: {sessionStorage.getItem('adminSessionId').slice(0, 12)}...
        </p>
      )}
      <p className="text-xs text-blue-600 mt-2">
        💡 Chỉ Admin đã đăng nhập mới có thể chỉnh sửa dữ liệu
      </p>
    </div>
  )}
</div>

<div className="mb-4">
  <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-3">Quản lý Rooms</h2>
  
  {/* Buttons - Mobile Friendly */}
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
    <button
      onClick={() => {
        setEditingRoom(null);
        setShowRoomForm(true);
      }}
      className="flex items-center justify-center gap-2 bg-purple-600 text-white px-3 py-2.5 rounded-lg hover:bg-purple-700 text-sm"
    >
      <Plus size={18} />
      Tạo Room mới
    </button>
    
    <button
      onClick={() => {
        setEditingMember(null);
        setShowMemberForm(true);
      }}
      className="flex items-center justify-center gap-2 bg-blue-600 text-white px-3 py-2.5 rounded-lg hover:bg-blue-700 text-sm"
    >
      <Users size={18} />
      Thêm thành viên
    </button>
    
    <button
      onClick={() => {
        setEditingTransaction(null);
        setShowTransactionForm(true);
      }}
      className="flex items-center justify-center gap-2 bg-green-600 text-white px-3 py-2.5 rounded-lg hover:bg-green-700 text-sm sm:col-span-2 lg:col-span-1"
    >
      <Plus size={18} />
      Thêm giao dịch
    </button>
  </div>
</div>

                {Array.isArray(rooms) && rooms.map(room => (
                    <div key={room.id} className="border rounded-lg p-4 bg-gray-50">
                   {/* Header Room - Mobile Responsive */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                        {/* Icon và tên */}
                        <div className="flex items-center gap-3">
                          <span className="text-3xl sm:text-4xl">{room.icon}</span>
                          <h3 className="font-semibold text-base sm:text-lg">{room.name}</h3>
                        </div>
                        
                        {/* Buttons - Grid trên mobile */}
                        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                        <button
  onClick={() => handleExportRoomToExcel(room)}
  className="flex items-center justify-center gap-1 sm:gap-2 bg-green-600 text-white px-2 sm:px-3 py-2 rounded-lg hover:bg-green-700 text-xs sm:text-sm"
  title="Xuất Excel"
>
  <Download size={14} className="sm:w-4 sm:h-4" />
  <span className="hidden sm:inline">Xuất Excel</span>
  <span className="sm:hidden">Excel</span>
</button>

<button
  onClick={() => setShowQRUpload(room)}
  className="flex items-center justify-center gap-1 sm:gap-2 bg-purple-600 text-white px-2 sm:px-3 py-2 rounded-lg hover:bg-purple-700 text-xs sm:text-sm"
  title="QR Code"
>
  📱
  <span className="hidden sm:inline">QR</span>
</button>

<button
  onClick={() => handleViewAllTransactions(room)}
  className="flex items-center justify-center gap-1 sm:gap-2 bg-teal-600 text-white px-2 sm:px-3 py-2 rounded-lg hover:bg-teal-700 text-xs sm:text-sm col-span-2 sm:col-span-1"
  title="Xem giao dịch"
>
  💰
  <span className="hidden sm:inline">Giao dịch</span>
  <span className="sm:hidden">GD</span>
  <span className="ml-1">({(room.transactions && Object.values(room.transactions).flat().length) || 0})</span>
</button>

<button
  onClick={() => handleEditRoom(room)}
  className="flex items-center justify-center gap-1 sm:gap-2 bg-yellow-600 text-white px-2 sm:px-3 py-2 rounded-lg hover:bg-yellow-700 text-xs sm:text-sm"
  title="Sửa Room"
>
  <Edit2 size={14} className="sm:w-4 sm:h-4" />
  <span className="hidden sm:inline">Sửa</span>
</button>

<label className="flex items-center justify-center gap-1 sm:gap-2 bg-blue-600 text-white px-2 sm:px-3 py-2 rounded-lg cursor-pointer hover:bg-blue-700 text-xs sm:text-sm">
  <Upload size={14} className="sm:w-4 sm:h-4" />
  <span className="hidden sm:inline">Import</span>
  <input
    type="file"
    accept=".xlsx,.xls"
    onChange={(e) => handleExcelUpload(e, room.id)}
    className="hidden"
  />
</label>

<button
  onClick={() => handleDeleteRoom(room.id)}
  className="flex items-center justify-center gap-1 sm:gap-2 bg-red-600 text-white px-2 sm:px-3 py-2 rounded-lg hover:bg-red-700 text-xs sm:text-sm"
  title="Xóa Room"
>
  <Trash2 size={14} className="sm:w-4 sm:h-4" />
  <span className="hidden sm:inline">Xóa</span>
</button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 md:gap-4 text-xs md:text-sm text-gray-600 mb-3">
                      <p>Số thành viên: <span className="font-semibold">{room.members.length}</span></p>
                     <p>Tổng điểm: <span className={`font-semibold ${
                        room.members.reduce((sum, m) => sum + (m.totalPoints || m.points[dateColumns[2]] || 0), 0) > 0 
                          ? 'text-blue-600' 
                          : room.members.reduce((sum, m) => sum + (m.totalPoints || m.points[dateColumns[2]] || 0), 0) < 0
                          ? 'text-red-600'
                          : 'text-gray-900'
                      }`}>
                        {room.members.reduce((sum, m) => sum + (m.totalPoints || m.points[dateColumns[2]] || 0), 0).toFixed(1)}
                      </span></p>
                    </div>
                    
{room.members.length > 0 && (
  <div className="mt-3 border-t pt-3">
    {/* ========================================= */}
    {/* 🆕 PHẦN MỚI: Ô TÌM KIẾM                */}
    {/* ========================================= */}
    <div className="mb-3">
      <div className="relative">
        {/* Icon kính lúp bên trái */}
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
        
        {/* Ô nhập tìm kiếm */}
        <input
          type="text"
          placeholder="Tìm kiếm thành viên trong room này..."
          value={adminRoomSearchTerms[room.id] || ''}
          onChange={(e) => {
            setAdminRoomSearchTerms({
              ...adminRoomSearchTerms,
              [room.id]: e.target.value
            });
          }}
          className="w-full pl-10 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        
        {/* Nút X để xóa tìm kiếm */}
        {adminRoomSearchTerms[room.id] && (
          <button
            onClick={() => {
              setAdminRoomSearchTerms({
                ...adminRoomSearchTerms,
                [room.id]: ''
              });
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>
    </div>

    {/* ========================================= */}
    {/* 🆕 PHẦN MỚI: Tiêu đề với số kết quả      */}
    {/* ========================================= */}
    <p className="text-xs font-semibold text-gray-700 mb-2">
      Danh sách thành viên 
      {adminRoomSearchTerms[room.id] ? (
        <span>
          {' '}({filterMembers(room.members, adminRoomSearchTerms[room.id]).length} / {room.members.length} kết quả)
        </span>
      ) : (
        <span> ({room.members.length})</span>
      )}
    </p>
    
    {/* ========================================= */}
    {/* Container danh sách - có scroll          */}
    {/* ========================================= */}
    <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg bg-gray-50 p-2">
      {(() => {
        // 🆕 Lọc danh sách thành viên theo từ khóa tìm kiếm
        const filteredMembers = filterMembers(
          room.members, 
          adminRoomSearchTerms[room.id] || ''
        );
        
        // 🆕 Nếu không tìm thấy ai
        if (filteredMembers.length === 0) {
          return (
            <div className="text-center py-4 text-gray-500 text-sm">
              Không tìm thấy thành viên phù hợp
            </div>
          );
        }
        
        // 🆕 Hiển thị danh sách đã lọc
        return filteredMembers.map(member => (
          <div 
            key={member.id} 
            className="bg-white rounded-lg hover:bg-blue-50 transition"
          >
            {/* ========================================= */}
            {/* DESKTOP: Hiển thị ngang                  */}
            {/* ========================================= */}
            <div className="hidden sm:flex items-center justify-between text-xs p-2">
              {/* Thông tin thành viên */}
              <div className="flex-1">
                <span className="font-medium">{member.name}</span>
                <span className="text-gray-500 ml-2">(ID: {member.id})</span>
                <span className={`ml-2 font-semibold ${
                  (member.totalPoints || member.points[dateColumns[2]] || 0) > 0 ? 'text-green-600' : 
                  (member.totalPoints || member.points[dateColumns[2]] || 0) < 0 ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {member.totalPoints || member.points[dateColumns[2]] || 0} điểm
                </span>
              </div>
              
              {/* Các nút chức năng */}
              <div className="flex gap-1">
                <button
                  onClick={() => setShowMemberHistory({ member, room })}
                  className="text-blue-600 hover:bg-blue-100 p-1.5 rounded"
                  title="Xem lịch sử"
                >
                  <Edit2 size={14} />
                </button>
                <button
                  onClick={() => handleEditMember(member, room.id)}
                  className="text-yellow-600 hover:bg-yellow-100 p-1.5 rounded"
                  title="Sửa thông tin"
                >
                  <Settings size={14} />
                </button>
                <button
                  onClick={() => handleDeleteMember(member.id, room.id)}
                  className="text-red-600 hover:bg-red-100 p-1.5 rounded"
                  title="Xóa"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            
            {/* ========================================= */}
            {/* MOBILE: Hiển thị dọc                     */}
            {/* ========================================= */}
            <div className="sm:hidden p-3">
              {/* Dòng 1: Tên và ID */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="font-medium text-sm">{member.name}</div>
                  <div className="text-gray-500 text-xs mt-0.5">ID: {member.id}</div>
                </div>
                
                {/* Điểm - nổi bật */}
                <div className={`text-right font-bold text-base ${
                  (member.totalPoints || member.points[dateColumns[2]] || 0) > 0 ? 'text-green-600' : 
                  (member.totalPoints || member.points[dateColumns[2]] || 0) < 0 ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {member.totalPoints || member.points[dateColumns[2]] || 0}
                  <div className="text-xs font-normal text-gray-500">điểm</div>
                </div>
              </div>
              
              {/* Dòng 2: Các nút chức năng - FULL WIDTH */}
              <div className="grid grid-cols-3 gap-1.5 mt-2">
                <button
                  onClick={() => setShowMemberHistory({ member, room })}
                  className="flex flex-col items-center justify-center bg-blue-50 text-blue-600 py-2 rounded text-xs hover:bg-blue-100"
                >
                  <Edit2 size={16} />
                  <span className="mt-1">Lịch sử</span>
                </button>
                
                <button
                  onClick={() => handleEditMember(member, room.id)}
                  className="flex flex-col items-center justify-center bg-yellow-50 text-yellow-600 py-2 rounded text-xs hover:bg-yellow-100"
                >
                  <Settings size={16} />
                  <span className="mt-1">Sửa</span>
                </button>
                
                <button
                  onClick={() => handleDeleteMember(member.id, room.id)}
                  className="flex flex-col items-center justify-center bg-red-50 text-red-600 py-2 rounded text-xs hover:bg-red-100"
                >
                  <Trash2 size={16} />
                  <span className="mt-1">Xóa</span>
                </button>
              </div>
            </div>
          </div>
        ));
      })()}
    </div>
  </div>
)}
                  </div>
                ))}

              
              </div>
            </div>
          </div>
        </div>

        {showTransactionForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-green-600 text-white p-4 flex justify-between items-center sticky top-0">
                <h2 className="text-xl font-bold">Thêm Giao Dịch Mới</h2>
                <button
                  onClick={() => setShowTransactionForm(false)}
                  className="text-white hover:bg-green-700 rounded-full p-2"
                >
                  ✕
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Chọn Room <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={transactionForm.roomId || ''}
                    onChange={(e) => setTransactionForm({...transactionForm, roomId: parseInt(e.target.value), delivererId: '', receiverId: ''})}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">-- Chọn Room --</option>
                    {rooms.map(room => (
                      <option key={room.id} value={room.id}>{room.name}</option>
                    ))}
                  </select>
                </div>
                   <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={transactionForm.isAddPointTransaction}
                            onChange={(e) => {
                              setTransactionForm({
                                ...transactionForm, 
                                isAddPointTransaction: e.target.checked,
                                isFreeTransaction: false,
                                manualPoints: '' // Reset điểm thủ công
                              });
                            }}
                            className="w-5 h-5 text-purple-600"
                            disabled={!transactionForm.roomId}
                          />
                          <div className="flex-1">
                            <span className="text-sm font-semibold text-purple-900">
                              Giao dịch cộng điểm
                            </span>
                            <p className="text-xs text-purple-700 mt-1">
                              Người giao bị TRỪ điểm, người nhận được CỘNG điểm (nhập thủ công số điểm)
                            </p>
                          </div>
                        </label>
                      </div>
                  <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
  <label className="flex items-center gap-3 cursor-pointer">
    <input
      type="checkbox"
      checked={transactionForm.isFreeTransaction}
      onChange={(e) => {
        setTransactionForm({
          ...transactionForm, 
          isFreeTransaction: e.target.checked,
          isAddPointTransaction: false
        });
      }}
      className="w-5 h-5 text-orange-600"
      disabled={!transactionForm.roomId}
    />
    <div className="flex-1">
      <span className="text-sm font-semibold text-orange-900">
        Giao Free (Không tính điểm)
      </span>
      <p className="text-xs text-orange-700 mt-1">
        Người giao nhận 0 điểm, người nhận trừ 0 điểm (chỉ ghi nhận giao dịch)
      </p>
    </div>
  </label>
</div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Ngày giao dịch <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={transactionForm.date}
                    onChange={(e) => setTransactionForm({...transactionForm, date: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
<div>
  <label className="block text-sm font-semibold text-gray-700 mb-2">
    Người Giao (Deliverer) <span className="text-red-500">*</span>
  </label>
  <div className="relative">
    <input
      type="text"
      value={delivererSearch}
      onChange={(e) => {
        setDelivererSearch(e.target.value);
        setShowDelivererDropdown(true);
        if (transactionForm.delivererId) {
          setTransactionForm({...transactionForm, delivererId: ''});
        }
      }}
      onFocus={() => setShowDelivererDropdown(true)}
      placeholder="Tìm kiếm người giao..."
      disabled={!transactionForm.roomId}
      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
    />
    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
    
    {showDelivererDropdown && transactionForm.roomId && (
      <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
        {filterMembers(
          rooms.find(r => r.id === transactionForm.roomId)?.members || [],
          delivererSearch
        ).length > 0 ? (
          filterMembers(
            rooms.find(r => r.id === transactionForm.roomId)?.members || [],
            delivererSearch
          ).map(member => (
            <div
              key={member.id}
              onClick={() => {
                setTransactionForm({...transactionForm, delivererId: member.id.toString()});
                setDelivererSearch(`${member.name} (ID: ${member.id})`);
                setShowDelivererDropdown(false);
              }}
              className={`px-4 py-2 cursor-pointer hover:bg-blue-50 ${
                transactionForm.delivererId === member.id.toString() ? 'bg-blue-100' : ''
              }`}
            >
              <div className="font-medium">{member.name}</div>
              <div className="text-xs text-gray-500">
                ID: {member.id} | Điểm: {member.totalPoints || member.points[dateColumns[2]] || 0}
              </div>
            </div>
          ))
        ) : (
          <div className="px-4 py-3 text-center text-gray-500 text-sm">
            Không tìm thấy thành viên
          </div>
        )}
      </div>
    )}
  </div>
  
  {transactionForm.delivererId && (
    <div className="mt-2 flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
      <span className="text-sm text-green-800">
        ✓ Đã chọn: {rooms.find(r => r.id === transactionForm.roomId)?.members.find(m => m.id === parseInt(transactionForm.delivererId))?.name}
      </span>
      <button
        onClick={() => {
          setTransactionForm({...transactionForm, delivererId: ''});
          setDelivererSearch('');
        }}
        className="text-red-600 hover:text-red-800 text-xs"
      >
        ✕ Xóa
      </button>
    </div>
  )}
  
  {transactionForm.isAddPointTransaction && (
    <p className="text-xs text-purple-700 mt-1">
      💡 Giao dịch cộng điểm: Người giao sẽ bị TRỪ điểm
    </p>
  )}
</div>


                <div>
  <label className="block text-sm font-semibold text-gray-700 mb-2">
    Người Nhận (Receiver) <span className="text-red-500">*</span>
  </label>
  <div className="relative">
    <input
      type="text"
      value={receiverSearch}
      onChange={(e) => {
        setReceiverSearch(e.target.value);
        setShowReceiverDropdown(true);
        if (transactionForm.receiverId) {
          setTransactionForm({...transactionForm, receiverId: ''});
        }
      }}
      onFocus={() => setShowReceiverDropdown(true)}
      placeholder="Tìm kiếm người nhận..."
      disabled={!transactionForm.roomId}
      className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100"
    />
    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
    
    {showReceiverDropdown && transactionForm.roomId && (
      <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
        {filterMembers(
          rooms.find(r => r.id === transactionForm.roomId)?.members || [],
          receiverSearch
        ).length > 0 ? (
          filterMembers(
            rooms.find(r => r.id === transactionForm.roomId)?.members || [],
            receiverSearch
          ).map(member => (
            <div
              key={member.id}
              onClick={() => {
                setTransactionForm({...transactionForm, receiverId: member.id.toString()});
                setReceiverSearch(`${member.name} (ID: ${member.id})`);
                setShowReceiverDropdown(false);
              }}
              className={`px-4 py-2 cursor-pointer hover:bg-blue-50 ${
                transactionForm.receiverId === member.id.toString() ? 'bg-blue-100' : ''
              }`}
            >
              <div className="font-medium">{member.name}</div>
              <div className="text-xs text-gray-500">
                ID: {member.id} | Điểm: {member.totalPoints || member.points[dateColumns[2]] || 0}
              </div>
            </div>
          ))
        ) : (
          <div className="px-4 py-3 text-center text-gray-500 text-sm">
            Không tìm thấy thành viên
          </div>
        )}
      </div>
    )}
  </div>
  
  {transactionForm.receiverId && (
    <div className="mt-2 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
      <span className="text-sm text-blue-800">
        ✓ Đã chọn: {rooms.find(r => r.id === transactionForm.roomId)?.members.find(m => m.id === parseInt(transactionForm.receiverId))?.name}
      </span>
      <button
        onClick={() => {
          setTransactionForm({...transactionForm, receiverId: ''});
          setReceiverSearch('');
        }}
        className="text-red-600 hover:text-red-800 text-xs"
      >
        ✕ Xóa
      </button>
    </div>
  )}
</div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Giá trị (VND) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={transactionForm.price ? parseFloat(transactionForm.price).toLocaleString('vi-VN') : ''}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d]/g, '');
                      setTransactionForm({...transactionForm, price: value});
                    }}
                    placeholder="Nhập giá trị giao dịch..."
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  {transactionForm.price && transactionForm.roomId && (
                    (() => {
                      if (transactionForm.isFreeTransaction) {
                        return (
                          <p className="text-sm text-orange-600 font-semibold mt-1">
                            Giao Free: 0 điểm (không tính điểm cho cả 2 bên)
                          </p>
                        );
                      } else if (transactionForm.isAddPointTransaction) {
                        return (
                          <p className="text-sm text-purple-600 font-semibold mt-1">
                            ⚠️ Giao dịch cộng điểm: Vui lòng nhập số điểm thủ công bên dưới
                          </p>
                        );
                      } else {
                        const room = rooms.find(r => r.id === transactionForm.roomId);
                        const points = room ? calculatePoints(parseFloat(transactionForm.price), room.rule) : null;
                        return points !== null ? (
                          <p className="text-sm text-green-600 font-semibold mt-1">
                            ✓ Điểm tự động: {points} điểm
                          </p>
                        ) : (
                          <p className="text-sm text-orange-600 font-semibold mt-1">
                            ⚠️ Giá trị vượt quy tắc! Cần nhập điểm thủ công bên dưới.
                          </p>
                        );
                      }
                    })()
                  )}
                </div>

                {!transactionForm.isFreeTransaction && (
                  <div className={`border-2 rounded-lg p-4 ${
                    transactionForm.isAddPointTransaction
                      ? 'bg-purple-50 border-purple-300'
                      : transactionForm.price && transactionForm.roomId &&
                        calculatePoints(parseFloat(transactionForm.price), rooms.find(r => r.id === transactionForm.roomId)?.rule) === null
                      ? 'bg-orange-50 border-orange-300'
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-semibold text-gray-800">
                        Điểm thủ công
                        {(transactionForm.isAddPointTransaction ||
                          (transactionForm.price && transactionForm.roomId &&
                          calculatePoints(parseFloat(transactionForm.price), rooms.find(r => r.id === transactionForm.roomId)?.rule) === null))
                          ? <span className="text-red-500 ml-1">*</span>
                          : <span className="text-gray-400 text-xs font-normal ml-2">(tùy chọn - để trống để dùng điểm tự động)</span>
                        }
                      </label>
                      {/* Nút xóa điểm thủ công */}
                      {transactionForm.manualPoints && (
                        <button
                          onClick={() => setTransactionForm({ ...transactionForm, manualPoints: '' })}
                          className="text-xs text-gray-500 hover:text-red-600 underline"
                        >
                          ✕ Xóa, dùng tự động
                        </button>
                      )}
                    </div>

                    <input
                      type="number"
                      step="0.1"
                      value={transactionForm.manualPoints}
                      onChange={(e) =>
                        setTransactionForm({ ...transactionForm, manualPoints: e.target.value })
                      }
                      onWheel={(e) => e.currentTarget.blur()}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
                      }}
                      inputMode="decimal"
                      placeholder={
                        transactionForm.isAddPointTransaction
                          ? 'Nhập số điểm cộng/trừ...'
                          : transactionForm.price && transactionForm.roomId
                          ? (() => {
                              const auto = calculatePoints(parseFloat(transactionForm.price), rooms.find(r => r.id === transactionForm.roomId)?.rule);
                              return auto !== null ? `Tự động: ${auto} điểm` : 'Vượt quy tắc, cần nhập thủ công...';
                            })()
                          : 'Nhập điểm thủ công (nếu muốn override)...'
                      }
                      className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 ${
                        transactionForm.isAddPointTransaction
                          ? 'border-purple-300 focus:ring-purple-500'
                          : transactionForm.price && transactionForm.roomId &&
                            calculatePoints(parseFloat(transactionForm.price), rooms.find(r => r.id === transactionForm.roomId)?.rule) === null
                          ? 'border-orange-300 focus:ring-orange-500'
                          : 'border-gray-300 focus:ring-blue-500'
                      }`}
                    />

                    <p className="text-xs text-gray-600 mt-2">
                      {transactionForm.isAddPointTransaction
                        ? '💡 Người giao bị TRỪ số điểm này, người nhận được CỘNG số điểm này'
                        : transactionForm.manualPoints
                        ? '✏️ Đang dùng điểm thủ công - nhấn "Xóa, dùng tự động" để quay lại điểm theo quy tắc'
                        : transactionForm.price && transactionForm.roomId
                        ? (() => {
                            const auto = calculatePoints(parseFloat(transactionForm.price), rooms.find(r => r.id === transactionForm.roomId)?.rule);
                            return auto !== null
                              ? `💡 Để trống = dùng điểm tự động (${auto} điểm). Nhập số để override.`
                              : `⚠️ Giá trị ${parseFloat(transactionForm.price || 0).toLocaleString('vi-VN')} VND vượt quy tắc, cần nhập thủ công.`;
                          })()
                        : '💡 Nhập số điểm để override quy tắc tự động của Room'
                      }
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Diễn giải <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={transactionForm.description}
                    onChange={(e) => setTransactionForm({...transactionForm, description: e.target.value})}
                    placeholder="Nhập mô tả chi tiết giao dịch..."
                    rows="3"
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {transactionForm.roomId && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm font-semibold text-blue-900 mb-2">Quy tắc chuyển đổi của Room:</p>
                    <ul className="text-xs text-blue-800 space-y-1">
                      {rooms.find(r => r.id === transactionForm.roomId)?.rule.map((r, i) => (
                        <li key={i}>
                          {r.min.toLocaleString('vi-VN')} - {r.max.toLocaleString('vi-VN')} VND → {r.points} điểm
                        </li>
                      ))}
                      <li className="font-semibold">Trên 10,000,000 VND → Admin set thủ công</li>
                    </ul>
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowTransactionForm(false);
                      setEditingTransaction(null);
                      setDelivererSearch('');
                      setReceiverSearch('');
                      setShowDelivererDropdown(false);
                      setShowReceiverDropdown(false);
                    }}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition font-semibold"
                  >
                    Hủy
                  </button>
                  <button
                    onClick={handleAddTransaction}
                    className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition font-semibold"
                  >
                    Thêm Giao Dịch
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showMemberForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-blue-600 text-white p-4 flex justify-between items-center sticky top-0">
                <h2 className="text-xl font-bold">Thêm Thành Viên Mới</h2>
                <button
                  onClick={() => setShowMemberForm(false)}
                  className="text-white hover:bg-blue-700 rounded-full p-2"
                >
                  ✕
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Chọn Room <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={memberForm.roomId || ''}
                    onChange={(e) => setMemberForm({...memberForm, roomId: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Chọn Room --</option>
                    {rooms.map(room => (
                      <option key={room.id} value={room.id}>{room.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    ID Thành Viên <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={memberForm.id}
                    onChange={(e) => setMemberForm({...memberForm, id: e.target.value})}
                    placeholder="Nhập ID (số nguyên, VD: 1, 2, 3...)"
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 ID phải là số duy nhất trong Room. Ví dụ: 0, 1, 2, 3...
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tên Thành Viên <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={memberForm.name}
                    onChange={(e) => setMemberForm({...memberForm, name: e.target.value})}
                    placeholder="Nhập tên thành viên..."
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                  {!editingMember && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm text-green-800 font-semibold">
                        ✅ Thành viên mới bắt đầu với <strong>0 điểm</strong>
                      </p>
                      <p className="text-xs text-green-700 mt-1">
                        💡 Sau khi thêm, dùng <strong>"Giao dịch cộng điểm"</strong> (Admin → thành viên) 
                        để cấp điểm — lịch sử sẽ được lưu đầy đủ.
                      </p>
                    </div>
                  )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Hạn thu KT (Ngày gia hạn)
                  </label>
                  <input
                    type="text"
                    value={memberForm.deadline}
                    onChange={(e) => setMemberForm({...memberForm, deadline: e.target.value})}
                    placeholder="VD: Tháng 12/2025"
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Có thể để trống hoặc nhập dạng text tùy ý
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Ghi chú
                  </label>
                  <input
                    type="text"
                    value={memberForm.note}
                    onChange={(e) => setMemberForm({...memberForm, note: e.target.value})}
                    placeholder="VD: RET1, Cọc..."
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm font-semibold text-blue-900 mb-2">📋 Thông tin:</p>
                  <ul className="text-xs text-blue-800 space-y-1">
                    <li>• Thành viên mới luôn bắt đầu với 0 điểm</li>
                    <li>• Dùng "Giao dịch cộng điểm" để cấp điểm ban đầu</li>
                    <li>• Khi sửa thông tin, điểm sẽ không bị thay đổi</li>
                    <li>• ID phải là số duy nhất trong Room</li>
                  </ul>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={editingMember ? handleUpdateMember : handleAddMember}
                    className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-semibold"
                  >
                    {editingMember ? 'Cập nhật Thành Viên' : 'Thêm Thành Viên'}
                  </button>
                  <button
                    onClick={() => {
                      setShowMemberForm(false);
                      setEditingMember(null);
                    }}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition font-semibold"
                  >
                    Hủy
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showRoomForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="bg-purple-600 text-white p-4 flex justify-between items-center sticky top-0">
                <h2 className="text-xl font-bold">{editingRoom ? 'Sửa Room' : 'Tạo Room Mới'}</h2>
                <button
                  onClick={() => {
                    setShowRoomForm(false);
                    setEditingRoom(null);
                  }}
                  className="text-white hover:bg-purple-700 rounded-full p-2"
                >
                  ✕
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tên Room <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={roomForm.name}
                    onChange={(e) => setRoomForm({...roomForm, name: e.target.value})}
                    placeholder="VD: [1-1 RETURN] ROOM LỊCH"
                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Icon/Emoji Room <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      value={roomForm.icon}
                      onChange={(e) => setRoomForm({...roomForm, icon: e.target.value})}
                      placeholder="Nhập emoji..."
                      className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      maxLength={2}
                    />
                    <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-4xl">{roomForm.icon || '🏠'}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    {['🏠', '🏢', '🏭', '🏬', '🏦', '🏪', '🏨', '🏛️'].map(emoji => (
                      <button
                        key={emoji}
                        onClick={() => setRoomForm({...roomForm, icon: emoji})}
                        className="text-3xl hover:scale-125 transition"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Mật khẩu bảo vệ (Tùy chọn)
                </label>
                <input
                  type="text"
                  value={roomForm.password}
                  onChange={(e) => setRoomForm({...roomForm, password: e.target.value})}
                  placeholder="Để trống nếu không cần mật khẩu"
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  💡 Người dùng phải nhập đúng mật khẩu mới vào được Room
                </p>
              </div>

              <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Quy tắc chuyển đổi điểm
                  </label>
                  <div className="space-y-3">
                    {roomForm.rule.map((r, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={r.min.toLocaleString('vi-VN')}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value.replace(/[^\d]/g, ''));
                            const newRule = [...roomForm.rule];
                            newRule[index].min = isNaN(val) ? 0 : val;
                            setRoomForm({...roomForm, rule: newRule});
                          }}
                          className="w-32 px-3 py-2 border rounded-lg text-sm"
                        />
                        <span>-</span>
                        <input
                          type="text"
                          value={r.max.toLocaleString('vi-VN')}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value.replace(/[^\d]/g, ''));
                            const newRule = [...roomForm.rule];
                            newRule[index].max = isNaN(val) ? 0 : val;
                            setRoomForm({...roomForm, rule: newRule});
                          }}
                          className="w-32 px-3 py-2 border rounded-lg text-sm"
                        />
                        <span>VND →</span>
                        <input
                          type="number"
                          step="0.1"
                          value={r.points}
                          onChange={(e) => {
                            const newRule = [...roomForm.rule];
                            newRule[index].points = parseFloat(e.target.value) || 0;
                            setRoomForm({...roomForm, rule: newRule});
                          }}
                          className="w-20 px-3 py-2 border rounded-lg text-sm"
                        />
                        <span>điểm</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Giá trị vượt quy tắc cuối cùng sẽ cần Admin nhập điểm thủ công
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={editingRoom ? handleUpdateRoom : handleCreateRoom}
                    className="flex-1 bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition font-semibold"
                  >
                    {editingRoom ? 'Cập nhật Room' : 'Tạo Room'}
                  </button>
                  <button
                    onClick={() => {
                      setShowRoomForm(false);
                      setEditingRoom(null);
                    }}
                    className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 transition font-semibold"
                  >
                    Hủy
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showDeleteMemberConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Xác nhận xóa thành viên</h3>
              <p className="text-gray-600 mb-6">
                Bạn có chắc chắn muốn xóa thành viên <span className="font-semibold">"{showDeleteMemberConfirm.memberName}"</span>?
                <br/><br/>
                Tất cả lịch sử giao dịch của thành viên này sẽ bị xóa!
              </p>
              <div className="flex gap-3">
                <button
                  onClick={confirmDeleteMember}
                  className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition font-semibold"
                >
                  Xóa
                </button>
                <button
                  onClick={() => setShowDeleteMemberConfirm(null)}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 rounded-lg hover:bg-gray-300 transition font-semibold"
                >
                  Hủy
                </button>
              </div>
            </div>
          </div>
        )}

        {showAllTransactions && selectedRoomTransactions && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
              <div className="bg-teal-600 text-white p-4 flex justify-between items-center">
                <h2 className="text-xl font-bold">Tất cả giao dịch - {selectedRoomTransactions.name}</h2>
                <button
                  onClick={() => setShowAllTransactions(false)}
                  className="text-white hover:bg-teal-700 rounded-full p-2"
                >
                  ✕
                </button>
              </div>
              
            <div className="overflow-auto max-h-[calc(90vh-80px)]">
              {/* Desktop View */}
              <table className="w-full hidden md:table">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-sm font-semibold">Ngày</th>
                    <th className="px-3 py-2 text-left text-sm font-semibold">Thành viên</th>
                    <th className="px-3 py-2 text-left text-sm font-semibold">Diễn giải</th>
                    <th className="px-3 py-2 text-right text-sm font-semibold">Giá tiền</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold">Vai trò</th>
                    <th className="px-3 py-2 text-left text-sm font-semibold">Đối tác</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold">Điểm</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {getAllTransactionsFlat(selectedRoomTransactions).map((trans, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm">{trans.date}</td>
                      <td className="px-3 py-2 text-sm font-medium">{trans.memberName}</td>
                      <td className="px-3 py-2 text-sm max-w-xs truncate" title={trans.description}>
                        {trans.description}
                      </td>
                      <td className="px-3 py-2 text-sm text-right">
                        {trans.price.toLocaleString('vi-VN')}
                      </td>
                      <td className="px-3 py-2 text-sm text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          trans.role === 'Giao' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {trans.role}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-sm">{trans.partner}</td>
                      <td className={`px-3 py-2 text-sm text-center font-semibold ${
                        trans.points > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {trans.points > 0 ? '+' : ''}{trans.points}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => handleDeleteTransaction(trans, selectedRoomTransactions)}
                          className="text-red-600 hover:bg-red-50 p-1 rounded"
                          title="Xóa"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile View */}
              <div className="md:hidden space-y-3 p-4">
                {getAllTransactionsFlat(selectedRoomTransactions).map((trans, index) => (
                  <div key={index} className="bg-white border rounded-lg p-4 shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="text-sm font-semibold text-gray-700">{trans.date}</div>
                        <div className="text-xs text-gray-600">{trans.memberName}</div>
                      </div>
                      <button
                        onClick={() => handleDeleteTransaction(trans, selectedRoomTransactions)}
                        className="text-red-600 hover:bg-red-50 p-2 rounded"
                        title="Xóa"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <p className="text-sm text-gray-800 mb-2 line-clamp-2">{trans.description}</p>
                    <div className="flex justify-between items-center mb-2">
                      <span className={`px-2 py-1 rounded text-xs ${
                        trans.role === 'Giao' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {trans.role}
                      </span>
                      <span className="text-sm text-gray-600">→ {trans.partner}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="font-semibold text-gray-900 text-sm">
                        {trans.price.toLocaleString('vi-VN')} VND
                      </div>
                      <div className={`font-bold text-sm ${
                        trans.points > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {trans.points > 0 ? '+' : ''}{trans.points} điểm
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {getAllTransactionsFlat(selectedRoomTransactions).length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  Chưa có giao dịch nào
                </div>
              )}
            </div>
            </div>
          </div>
        )}

        {showQRUpload && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">QR Code - {showQRUpload.name}</h3>
              
              {showQRUpload.qrCode ? (
                <div className="space-y-4">
                  <img src={showQRUpload.qrCode} alt="QR Code" className="w-full max-w-xs mx-auto rounded-lg border" />
                  <div className="flex gap-3">
                    <label className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition text-center cursor-pointer">
                      Thay đổi QR
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleUploadQR(e, showQRUpload.id)}
                        className="hidden"
                      />
                    </label>
                    <button
                      onClick={() => handleRemoveQR(showQRUpload.id)}
                      className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition"
                    >
                      Xóa QR
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                    <p className="text-gray-500 mb-4">Chưa có QR Code</p>
                    <label className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition cursor-pointer inline-block">
                      Upload QR Code
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleUploadQR(e, showQRUpload.id)}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              )}
              
              <button
                onClick={() => setShowQRUpload(null)}
                className="w-full mt-4 bg-gray-200 text-gray-800 py-2 rounded-lg hover:bg-gray-300 transition"
              >
                Đóng
              </button>
            </div>
          </div>
        )}
{showMemberHistory && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
    {/* 🆕 Container modal - tối ưu kích thước mobile */}
    <div className="bg-white rounded-lg shadow-2xl w-full h-full sm:h-auto sm:max-w-6xl sm:max-h-[90vh] flex flex-col overflow-hidden">
      
      {/* ========================================= */}
      {/* HEADER - Sticky trên cùng                */}
      {/* ========================================= */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-3 sm:p-4 flex-shrink-0">
        <div className="flex justify-between items-center">
          <div className="flex-1 min-w-0">
            <h2 className="text-base sm:text-xl font-bold truncate">
              Lịch sử - {showMemberHistory.member.name}
            </h2>
            <p className="text-xs sm:text-sm text-blue-100 truncate">
              Room: {showMemberHistory.room.name} | 
              Điểm: {showMemberHistory.member.points[dateColumns[2]] || 0}
            </p>
          </div>
          <button
            onClick={() => {
              setShowMemberHistory(null);
              setEditingHistoryTransaction(null);
            }}
            className="text-white hover:bg-blue-800 rounded-full p-2 ml-2 flex-shrink-0"
          >
            ✕
          </button>
        </div>
      </div>
      
      {/* ========================================= */}
      {/* CONTENT - Scrollable area                */}
      {/* ========================================= */}
      <div className="flex-1 overflow-auto">
        {/* ============ DESKTOP VIEW ============ */}
        <div className="hidden sm:block">
          <table className="w-full">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left text-sm font-semibold">Ngày</th>
                <th className="px-3 py-2 text-left text-sm font-semibold">Diễn giải</th>
                <th className="px-3 py-2 text-right text-sm font-semibold">Giá tiền</th>
                <th className="px-3 py-2 text-center text-sm font-semibold">Vai trò</th>
                <th className="px-3 py-2 text-left text-sm font-semibold">Đối tác</th>
                <th className="px-3 py-2 text-center text-sm font-semibold">Điểm</th>
                <th className="px-3 py-2 text-center text-sm font-semibold">Thao tác</th>
              </tr>
            </thead>
            <tbody>
            {showMemberHistory.room.transactions[showMemberHistory.member.id]?.length > 0 ? (
              [...showMemberHistory.room.transactions[showMemberHistory.member.id]]
                .map((trans, originalIndex) => ({ ...trans, originalIndex }))  // ✅ Thêm index gốc
                .sort((a, b) => {
                  const dateCompare = parseTransactionDate(b.date) - parseTransactionDate(a.date);
                  
                  if (dateCompare === 0) {
                    return b.originalIndex - a.originalIndex;
                  }
                  
                  return dateCompare;
                })
                .map((trans, displayIndex) => {
                  const actualIndex = trans.originalIndex;  // ✅ Dùng originalIndex thay vì tính toán
                    
                    return editingHistoryTransaction?.index === actualIndex ? (
                      // MODE EDIT
                      <tr key={actualIndex} className="border-b bg-yellow-50">
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={editingHistoryTransaction.date}
                            onChange={(e) => setEditingHistoryTransaction({
                              ...editingHistoryTransaction,
                              date: e.target.value
                            })}
                            className="w-20 px-2 py-1 border rounded text-sm"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <textarea
                            value={editingHistoryTransaction.description}
                            onChange={(e) => setEditingHistoryTransaction({
                              ...editingHistoryTransaction,
                              description: e.target.value
                            })}
                            className="w-full px-2 py-1 border rounded text-sm"
                            rows="2"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={editingHistoryTransaction.price.toLocaleString('vi-VN')}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^\d]/g, '');
                              setEditingHistoryTransaction({
                                ...editingHistoryTransaction,
                                price: parseFloat(value) || 0
                              });
                            }}
                            className="w-28 px-2 py-1 border rounded text-sm text-right"
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <select
                            value={editingHistoryTransaction.role}
                            onChange={(e) => setEditingHistoryTransaction({
                              ...editingHistoryTransaction,
                              role: e.target.value
                            })}
                            className="px-2 py-1 border rounded text-sm"
                          >
                            <option value="Giao">Giao</option>
                            <option value="Nhận">Nhận</option>
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={editingHistoryTransaction.partner}
                            onChange={(e) => setEditingHistoryTransaction({
                              ...editingHistoryTransaction,
                              partner: e.target.value
                            })}
                            className="w-full px-2 py-1 border rounded text-sm"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="0.1"
                            value={editingHistoryTransaction.points}
                            onChange={(e) => setEditingHistoryTransaction({
                              ...editingHistoryTransaction,
                              points: parseFloat(e.target.value) || 0
                            })}
                            className="w-20 px-2 py-1 border rounded text-sm text-center"
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex gap-1 justify-center">
                            <button
                              onClick={() => {
                                const room = showMemberHistory.room;
                                const member = showMemberHistory.member;
                                const oldTrans = room.transactions[member.id][actualIndex];
                                const newTrans = editingHistoryTransaction;

                                const pointsDiff = parseFloat((newTrans.points - oldTrans.points).toFixed(1));
                                const currentDate = dateColumns[2];

                                // ✅ Tìm partner để điều chỉnh điểm đối ứng
                                const partnerMember = room.members.find(m => m.name === newTrans.partner);

                                // Cập nhật giao dịch của member
                                const updatedMemberTransactions = [...room.transactions[member.id]];
                                updatedMemberTransactions[actualIndex] = {
                                  date: newTrans.date,
                                  description: newTrans.description,
                                  price: newTrans.price,
                                  role: newTrans.role,
                                  partner: newTrans.partner,
                                  points: newTrans.points
                                };

                                // ✅ Cập nhật giao dịch cặp của partner
                                let updatedPartnerTransactions = null;
                                if (partnerMember && pointsDiff !== 0) {
                                  const partnerTrans = room.transactions[partnerMember.id] || [];
                                  const partnerTIndex = partnerTrans.findIndex(t =>
                                    t.date === oldTrans.date &&
                                    t.description === oldTrans.description &&
                                    Math.abs(t.price - oldTrans.price) < 0.01 &&
                                    t.partner === member.name
                                  );
                                  if (partnerTIndex !== -1) {
                                    updatedPartnerTransactions = [...partnerTrans];
                                    updatedPartnerTransactions[partnerTIndex] = {
                                      ...updatedPartnerTransactions[partnerTIndex],
                                      points: parseFloat(
                                        (updatedPartnerTransactions[partnerTIndex].points - pointsDiff).toFixed(1)
                                      )
                                    };
                                  }
                                }

                                setRooms(rooms.map(r => {
                                  if (r.id !== room.id) return r;

                                  const newTransactions = {
                                    ...r.transactions,
                                    [member.id]: updatedMemberTransactions,
                                    ...(partnerMember && updatedPartnerTransactions
                                      ? { [partnerMember.id]: updatedPartnerTransactions }
                                      : {})
                                  };

                                  return {
                                    ...r,
                                    transactions: newTransactions,
                                    members: r.members.map(m => {
                                      // Cập nhật điểm member đang sửa
                                      if (m.id === member.id && pointsDiff !== 0) {
                                        const newTotal = parseFloat((m.totalPoints + pointsDiff).toFixed(1));
                                        return {
                                          ...m,
                                          points: { ...m.points, [currentDate]: newTotal },
                                          totalPoints: newTotal
                                        };
                                      }
                                      // ✅ Cập nhật điểm partner - đảo ngược
                                      if (partnerMember && m.id === partnerMember.id && pointsDiff !== 0) {
                                        const newTotal = parseFloat((m.totalPoints - pointsDiff).toFixed(1));
                                        return {
                                          ...m,
                                          points: { ...m.points, [currentDate]: newTotal },
                                          totalPoints: newTotal
                                        };
                                      }
                                      return m;
                                    })
                                  };
                                }));

                                setEditingHistoryTransaction(null);

                                // ✅ Thông báo kết quả
                                if (pointsDiff === 0) {
                                  alert('Đã cập nhật giao dịch! (Điểm không thay đổi)');
                                } else if (partnerMember) {
                                  alert(
                                    `Đã cập nhật giao dịch!\n\n` +
                                    `↕️ ${member.name}: ${pointsDiff > 0 ? '+' : ''}${pointsDiff} điểm\n` +
                                    `↕️ ${partnerMember.name}: ${pointsDiff > 0 ? '-' : '+'}${Math.abs(pointsDiff)} điểm (đối ứng)`
                                  );
                                } else {
                                  alert(
                                    `Đã cập nhật giao dịch!\n\n` +
                                    `↕️ ${member.name}: ${pointsDiff > 0 ? '+' : ''}${pointsDiff} điểm\n` +
                                    `⚠️ Không tìm thấy đối tác "${newTrans.partner}" để cân bằng.`
                                  );
                                }
                              }}
                              className="text-green-600 hover:bg-green-50 p-1 rounded"
                              title="Lưu"
                            >
                              ✓
                            </button>
                            <button
                              onClick={() => setEditingHistoryTransaction(null)}
                              className="text-gray-600 hover:bg-gray-50 p-1 rounded"
                              title="Hủy"
                            >
                              ✕
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      // MODE VIEW
                      <tr key={actualIndex} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm">{trans.date}</td>
                        <td className="px-3 py-2 text-sm max-w-md">{trans.description}</td>
                        <td className="px-3 py-2 text-sm text-right">
                          {trans.price.toLocaleString('vi-VN')}
                        </td>
                        <td className="px-3 py-2 text-sm text-center">
                          <span className={`px-2 py-1 rounded text-xs ${
                            trans.role === 'Giao' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {trans.role}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-sm">{trans.partner}</td>
                        <td className={`px-3 py-2 text-sm text-center font-semibold ${
                          trans.points > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {trans.points > 0 ? '+' : ''}{trans.points}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex gap-1 justify-center">
                            <button
                              onClick={() => setEditingHistoryTransaction({ ...trans, index: actualIndex })}
                              className="text-yellow-600 hover:bg-yellow-50 p-1 rounded"
                              title="Sửa"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => {
                                const confirmDelete = window.confirm('Bạn có chắc chắn muốn xóa giao dịch này?');
                                if (!confirmDelete) return;

                                const transToDelete = trans;
                                const room = showMemberHistory.room;
                                const member = showMemberHistory.member;
                                
                                const updatedTransactions = room.transactions[member.id].filter((_, i) => i !== actualIndex);
                                
                                const currentDate = dateColumns[2];
                                const isFreeTransaction = transToDelete.role === 'Giao Free' || transToDelete.role === 'Nhận Free';
                                
                                setRooms(rooms.map(r => {
                                  if (r.id !== room.id) return r;
                                  
                                  return {
                                    ...r,
                                    transactions: {
                                      ...r.transactions,
                                      [member.id]: updatedTransactions
                                    },
                                    members: r.members.map(m => {
                                      if (m.id !== member.id || isFreeTransaction) return m;
                                      
                                      const pointsToRevert = -transToDelete.points;
                                      const newTotal = Math.round((m.totalPoints + pointsToRevert) * 10) / 10;
                                      
                                      return {
                                        ...m,
                                        points: {
                                          ...m.points,
                                          [currentDate]: newTotal
                                        },
                                        totalPoints: newTotal
                                      };
                                    })
                                  };
                                }));
                                
                                const updatedRoom = rooms.find(r => r.id === room.id);
                                if (updatedRoom) {
                                  const updatedMember = updatedRoom.members.find(m => m.id === member.id);
                                  setShowMemberHistory({
                                    room: updatedRoom,
                                    member: updatedMember
                                  });
                                }
                                
                                alert('Đã xóa giao dịch và cập nhật điểm!');
                              }}
                              className="text-red-600 hover:bg-red-50 p-1 rounded"
                              title="Xóa"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
              ) : (
                <tr>
                  <td colSpan="7" className="px-3 py-8 text-center text-gray-500">
                    Chưa có lịch sử giao dịch
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ============ MOBILE VIEW ============ */}
        <div className="sm:hidden p-3 space-y-3">
          {showMemberHistory.room.transactions[showMemberHistory.member.id]?.length > 0 ? (
            [...showMemberHistory.room.transactions[showMemberHistory.member.id]]
              .map((trans, originalIndex) => ({ ...trans, originalIndex }))
              .sort((a, b) => {
                const dateCompare = parseTransactionDate(b.date) - parseTransactionDate(a.date);
                
                if (dateCompare === 0) {
                  return b.originalIndex - a.originalIndex;
                }
                
                return dateCompare;
              })
              .map((trans, displayIndex) => {
                const actualIndex = trans.originalIndex;  // ✅ Dùng originalIndex thay vì tính toán
                
                return editingHistoryTransaction?.index === actualIndex ? (
                  // 🆕 MOBILE EDIT MODE
                  <div key={actualIndex} className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-3 space-y-3">
                    <div className="text-sm font-semibold text-yellow-900 mb-2">✏️ Chế độ chỉnh sửa</div>
                    
                    <div>
                      <label className="text-xs font-semibold text-gray-700">Ngày</label>
                      <input
                        type="text"
                        value={editingHistoryTransaction.date}
                        onChange={(e) => setEditingHistoryTransaction({
                          ...editingHistoryTransaction,
                          date: e.target.value
                        })}
                        className="w-full px-3 py-2 border rounded text-sm mt-1"
                      />
                    </div>
                    
                    <div>
                      <label className="text-xs font-semibold text-gray-700">Diễn giải</label>
                      <textarea
                        value={editingHistoryTransaction.description}
                        onChange={(e) => setEditingHistoryTransaction({
                          ...editingHistoryTransaction,
                          description: e.target.value
                        })}
                        className="w-full px-3 py-2 border rounded text-sm mt-1"
                        rows="3"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs font-semibold text-gray-700">Giá tiền</label>
                        <input
                          type="text"
                          value={editingHistoryTransaction.price.toLocaleString('vi-VN')}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^\d]/g, '');
                            setEditingHistoryTransaction({
                              ...editingHistoryTransaction,
                              price: parseFloat(value) || 0
                            });
                          }}
                          className="w-full px-3 py-2 border rounded text-sm mt-1"
                        />
                      </div>
                      
                      <div>
                        <label className="text-xs font-semibold text-gray-700">Điểm</label>
                        <input
                          type="number"
                          step="0.1"
                          value={editingHistoryTransaction.points}
                          onChange={(e) => setEditingHistoryTransaction({
                            ...editingHistoryTransaction,
                            points: parseFloat(e.target.value) || 0
                          })}
                          className="w-full px-3 py-2 border rounded text-sm mt-1"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs font-semibold text-gray-700">Vai trò</label>
                        <select
                          value={editingHistoryTransaction.role}
                          onChange={(e) => setEditingHistoryTransaction({
                            ...editingHistoryTransaction,
                            role: e.target.value
                          })}
                          className="w-full px-3 py-2 border rounded text-sm mt-1"
                        >
                          <option value="Giao">Giao</option>
                          <option value="Nhận">Nhận</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="text-xs font-semibold text-gray-700">Đối tác</label>
                        <input
                          type="text"
                          value={editingHistoryTransaction.partner}
                          onChange={(e) => setEditingHistoryTransaction({
                            ...editingHistoryTransaction,
                            partner: e.target.value
                          })}
                          className="w-full px-3 py-2 border rounded text-sm mt-1"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 pt-2">
                      <button
                        onClick={() => {
                          const room = showMemberHistory.room;
                          const member = showMemberHistory.member;
                          const oldTrans = room.transactions[member.id][actualIndex];
                          const newTrans = editingHistoryTransaction;

                          const pointsDiff = parseFloat((newTrans.points - oldTrans.points).toFixed(1));
                          const currentDate = dateColumns[2];

                          // ✅ Tìm partner để điều chỉnh điểm đối ứng
                          const partnerMember = room.members.find(m => m.name === newTrans.partner);

                          // Cập nhật giao dịch của member
                          const updatedMemberTransactions = [...room.transactions[member.id]];
                          updatedMemberTransactions[actualIndex] = {
                            date: newTrans.date,
                            description: newTrans.description,
                            price: newTrans.price,
                            role: newTrans.role,
                            partner: newTrans.partner,
                            points: newTrans.points
                          };

                          // ✅ Cập nhật giao dịch cặp của partner
                          let updatedPartnerTransactions = null;
                          if (partnerMember && pointsDiff !== 0) {
                            const partnerTrans = room.transactions[partnerMember.id] || [];
                            const partnerTIndex = partnerTrans.findIndex(t =>
                              t.date === oldTrans.date &&
                              t.description === oldTrans.description &&
                              Math.abs(t.price - oldTrans.price) < 0.01 &&
                              t.partner === member.name
                            );
                            if (partnerTIndex !== -1) {
                              updatedPartnerTransactions = [...partnerTrans];
                              updatedPartnerTransactions[partnerTIndex] = {
                                ...updatedPartnerTransactions[partnerTIndex],
                                points: parseFloat(
                                  (updatedPartnerTransactions[partnerTIndex].points - pointsDiff).toFixed(1)
                                )
                              };
                            }
                          }

                          setRooms(rooms.map(r => {
                            if (r.id !== room.id) return r;

                            const newTransactions = {
                              ...r.transactions,
                              [member.id]: updatedMemberTransactions,
                              ...(partnerMember && updatedPartnerTransactions
                                ? { [partnerMember.id]: updatedPartnerTransactions }
                                : {})
                            };

                            return {
                              ...r,
                              transactions: newTransactions,
                              members: r.members.map(m => {
                                // Cập nhật điểm member đang sửa
                                if (m.id === member.id && pointsDiff !== 0) {
                                  const newTotal = parseFloat((m.totalPoints + pointsDiff).toFixed(1));
                                  return {
                                    ...m,
                                    points: { ...m.points, [currentDate]: newTotal },
                                    totalPoints: newTotal
                                  };
                                }
                                // ✅ Cập nhật điểm partner - đảo ngược
                                if (partnerMember && m.id === partnerMember.id && pointsDiff !== 0) {
                                  const newTotal = parseFloat((m.totalPoints - pointsDiff).toFixed(1));
                                  return {
                                    ...m,
                                    points: { ...m.points, [currentDate]: newTotal },
                                    totalPoints: newTotal
                                  };
                                }
                                return m;
                              })
                            };
                          }));

                          setEditingHistoryTransaction(null);

                          // ✅ Thông báo kết quả
                          if (pointsDiff === 0) {
                            alert('Đã cập nhật giao dịch! (Điểm không thay đổi)');
                          } else if (partnerMember) {
                            alert(
                              `Đã cập nhật giao dịch!\n\n` +
                              `↕️ ${member.name}: ${pointsDiff > 0 ? '+' : ''}${pointsDiff} điểm\n` +
                              `↕️ ${partnerMember.name}: ${pointsDiff > 0 ? '-' : '+'}${Math.abs(pointsDiff)} điểm (đối ứng)`
                            );
                          } else {
                            alert(
                              `Đã cập nhật giao dịch!\n\n` +
                              `↕️ ${member.name}: ${pointsDiff > 0 ? '+' : ''}${pointsDiff} điểm\n` +
                              `⚠️ Không tìm thấy đối tác "${newTrans.partner}" để cân bằng.`
                            );
                          }
                        }}
                        className="bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 font-semibold text-sm"
                      >
                        ✓ Lưu
                      </button>
                      <button
                        onClick={() => setEditingHistoryTransaction(null)}
                        className="bg-gray-200 text-gray-800 py-2 rounded-lg hover:bg-gray-300 font-semibold text-sm"
                      >
                        ✕ Hủy
                      </button>
                    </div>
                  </div>
                ) : (
                  // 🆕 MOBILE VIEW MODE
                  <div key={actualIndex} className="bg-white border rounded-lg p-3 shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                      <div className="text-sm font-semibold text-gray-700">{trans.date}</div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        trans.role === 'Giao' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {trans.role}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-800 mb-2 line-clamp-2">{trans.description}</p>
                    
                    <div className="text-xs text-gray-600 mb-2">
                      Đối tác: <span className="font-medium">{trans.partner}</span>
                    </div>
                    
                    <div className="flex justify-between items-center mb-3">
                      <div className="font-semibold text-gray-900">
                        {trans.price.toLocaleString('vi-VN')} VND
                      </div>
                      <div className={`font-bold text-base ${
                        trans.points > 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {trans.points > 0 ? '+' : ''}{trans.points} điểm
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setEditingHistoryTransaction({ ...trans, index: actualIndex })}
                        className="flex items-center justify-center gap-1 bg-yellow-50 text-yellow-700 py-2 rounded hover:bg-yellow-100 text-xs font-medium"
                      >
                        <Edit2 size={14} />
                        Sửa
                      </button>
                      <button
                        onClick={() => {
                          const confirmDelete = window.confirm('Bạn có chắc chắn muốn xóa giao dịch này?');
                          if (!confirmDelete) return;

                          const transToDelete = trans;
                          const room = showMemberHistory.room;
                          const member = showMemberHistory.member;
                          
                          const updatedTransactions = room.transactions[member.id].filter((_, i) => i !== actualIndex);
                          
                          const currentDate = dateColumns[2];
                          const isFreeTransaction = transToDelete.role === 'Giao Free' || transToDelete.role === 'Nhận Free';
                          
                          setRooms(rooms.map(r => {
                            if (r.id !== room.id) return r;
                            
                            return {
                              ...r,
                              transactions: {
                                ...r.transactions,
                                [member.id]: updatedTransactions
                              },
                              members: r.members.map(m => {
                                if (m.id !== member.id || isFreeTransaction) return m;
                                
                                const pointsToRevert = -transToDelete.points;
                                const newTotal = Math.round((m.totalPoints + pointsToRevert) * 10) / 10;
                                
                                return {
                                  ...m,
                                  points: {
                                    ...m.points,
                                    [currentDate]: newTotal
                                  },
                                  totalPoints: newTotal
                                };
                              })
                            };
                          }));
                          
                          const updatedRoom = rooms.find(r => r.id === room.id);
                          if (updatedRoom) {
                            const updatedMember = updatedRoom.members.find(m => m.id === member.id);
                            setShowMemberHistory({
                              room: updatedRoom,
                              member: updatedMember
                            });
                          }
                          
                          alert('Đã xóa giao dịch và cập nhật điểm!');
                        }}
                        className="flex items-center justify-center gap-1 bg-red-50 text-red-600 py-2 rounded hover:bg-red-100 text-xs font-medium"
                      >
                        <Trash2 size={14} />
                        Xóa
                      </button>
                    </div>
                  </div>
                );
              })
          ) : (
            <div className="text-center py-8 text-gray-500">
              Chưa có lịch sử giao dịch
            </div>
          )}
        </div>
      </div>
      
      {/* ========================================= */}
      {/* FOOTER - Sticky dưới cùng                */}
      {/* ========================================= */}
      <div className="bg-gray-50 p-3 sm:p-4 border-t flex-shrink-0">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="text-xs sm:text-sm text-gray-600">
            <span className="font-semibold">Tổng giao dịch:</span>{' '}
            {showMemberHistory.room.transactions[showMemberHistory.member.id]?.length || 0}
          </div>
          <button
            onClick={() => {
              setShowMemberHistory(null);
              setEditingHistoryTransaction(null);
            }}
            className="w-full sm:w-auto bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition font-semibold text-sm"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  </div>
)}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-2">
            Tổng kết đến 23h59 hàng ngày {selectedRoom?.name}
          </h1>
          <p className="text-gray-600 text-sm md:text-base mb-4">
            Vui lòng bấm vào tên thành viên để xem lịch sử trước khi thắc mắc điểm.
          </p>
          
          <div className="flex flex-col md:flex-row gap-4">
            <button
              onClick={() => setCurrentView('home')}
              className="flex items-center justify-center gap-2 bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition"
            >
              <ArrowLeft size={18} />
              Quay lại trang chủ
            </button>
            
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Tìm kiếm thành viên..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full">
  <thead className="bg-blue-600 text-white">
    <tr>
      <th className="px-2 md:px-4 py-1.5 md:py-3 text-left font-semibold text-xs md:text-sm">Tên thành viên</th>
      {dateColumns.map(date => (
        <th key={date} className="px-1 md:px-4 py-1.5 md:py-3 text-center font-semibold text-xs md:text-sm">
          <span className="hidden md:inline">{date}</span>
          <span className="md:hidden">{date.substring(0, 5)}</span>
        </th>
      ))}
      <th className="px-2 md:px-4 py-1.5 md:py-3 text-center font-semibold text-xs md:text-sm">Hạn thu KT</th>
      <th className="px-2 md:px-4 py-1.5 md:py-3 text-center font-semibold text-xs md:text-sm">Ghi chú</th>
    </tr>
  </thead>
  <tbody>
    {paginatedMembers.map((member, index) => (
      <tr
        key={member.id}
        className={`border-b hover:bg-blue-50 transition cursor-pointer ${
          index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
        }`}
        onClick={() => handleMemberClick(member)}
      >
        <td className="px-2 md:px-4 py-1.5 md:py-3 text-blue-600 font-medium hover:underline text-xs md:text-base">
          {member.name}
        </td>
        {dateColumns.map((date, index) => {
          // ✅ FIX: Với cột ngày mới nhất (index 2), ưu tiên hiển thị totalPoints
          let displayPoint;
          if (index === 2) {
            // Cột ngày hôm nay: hiển thị totalPoints (điểm tích lũy hiện tại)
            displayPoint = member.totalPoints !== undefined 
              ? member.totalPoints 
              : (member.points[date] !== undefined ? member.points[date] : 0);
          } else {
            // 2 cột ngày cũ: hiển thị theo ngày
            displayPoint = member.points[date] !== undefined 
              ? member.points[date] 
              : 0;
          }
            
          return (
            <td
              key={date}
              className={`px-1 md:px-4 py-1.5 md:py-3 text-center font-semibold text-xs md:text-base ${getPointColor(displayPoint)}`}
            >
              {displayPoint}
            </td>
          );
        })}
        <td className="px-2 md:px-4 py-1.5 md:py-3 text-center text-[10px] md:text-sm">{member.deadline}</td>
        <td className="px-2 md:px-4 py-1.5 md:py-3 text-center text-[10px] md:text-sm">{member.note}</td>
      </tr>
    ))}
  </tbody>
</table>
          </div>

          {totalPages > 1 && (
            <div className="flex flex-wrap justify-center gap-2 p-4 bg-gray-50 border-t">
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`px-3 py-1 rounded ${
                    currentPage === i + 1
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>

      </div>

      {showModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="bg-blue-600 text-white p-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">Lịch sử: {selectedMember.name}</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-white hover:bg-blue-700 rounded-full p-2"
              >
                ✕
              </button>
            </div>
            
            <div className="overflow-auto max-h-[calc(90vh-80px)]">
              {/* Desktop View - Table */}
              <table className="w-full hidden md:table">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-sm font-semibold">Ngày</th>
                    <th className="px-3 py-2 text-left text-sm font-semibold">Diễn Giải</th>
                    <th className="px-3 py-2 text-right text-sm font-semibold">Giá Tiền</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold">Vai Trò</th>
                    <th className="px-3 py-2 text-left text-sm font-semibold">Đối Tác</th>
                    <th className="px-3 py-2 text-center text-sm font-semibold">Điểm (+/-)</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedRoom.transactions[selectedMember.id]?.length > 0 ? (
                  [...selectedRoom.transactions[selectedMember.id]]
                    .map((trans, originalIndex) => ({ ...trans, originalIndex }))  // ✅ Thêm index gốc
                    .sort((a, b) => {
                      const dateCompare = parseTransactionDate(b.date) - parseTransactionDate(a.date);
                      
                      // ✅ Nếu cùng ngày, giao dịch SAU (index cao) hiển thị TRƯỚC
                      if (dateCompare === 0) {
                        return b.originalIndex - a.originalIndex;
                      }
                      
                      return dateCompare;
                    })
                    .map((trans, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm">{trans.date}</td>
                        <td className="px-3 py-2 text-sm">{trans.description}</td>
                        <td className="px-3 py-2 text-sm text-right">
                          {trans.price.toLocaleString('vi-VN')}
                        </td>
                        <td className="px-3 py-2 text-sm text-center">
                          <span className={`px-2 py-1 rounded ${
                            trans.role === 'Giao' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {trans.role}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-sm">{trans.partner}</td>
                        <td className={`px-3 py-2 text-sm text-center font-semibold ${
                          trans.points > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {trans.points > 0 ? '+' : ''}{trans.points}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-3 py-8 text-center text-gray-500">
                        Chưa có lịch sử giao dịch
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

                {/* Mobile View - Cards */}
              <div className="md:hidden space-y-3 p-4">
                {selectedRoom.transactions[selectedMember.id]?.length > 0 ? (
                  [...selectedRoom.transactions[selectedMember.id]]
                    .map((trans, originalIndex) => ({ ...trans, originalIndex }))
                    .sort((a, b) => {
                      const dateCompare = parseTransactionDate(b.date) - parseTransactionDate(a.date);
                      
                      if (dateCompare === 0) {
                        return b.originalIndex - a.originalIndex;
                      }
                      
                      return dateCompare;
                    })
                    .map((trans, index) => (
                    <div key={index} className="bg-white border rounded-lg p-4 shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm font-semibold text-gray-700">{trans.date}</span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          trans.role === 'Giao' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {trans.role}
                        </span>
                      </div>
                      <p className="text-sm text-gray-800 mb-2">{trans.description}</p>
                      <div className="flex justify-between items-center text-sm">
                        <div>
                          <span className="text-gray-600">Đối tác: </span>
                          <span className="font-medium">{trans.partner}</span>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-gray-900">
                            {trans.price.toLocaleString('vi-VN')} VND
                          </div>
                          <div className={`font-bold ${
                            trans.points > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {trans.points > 0 ? '+' : ''}{trans.points} điểm
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-8">Chưa có lịch sử giao dịch</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
;
}
export default RoomManagementSystem;
